import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  keyframes,
  Menu,
  MenuItem,
  Modal,
  Snackbar,
  styled,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  Add,
  Check,
  ChevronLeft,
  Close,
  Edit,
  Fastfood,
  KeyboardArrowDown
} from "@mui/icons-material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as LockIcon } from "../../assets/lock1.svg";
import { ReactComponent as NoEntry } from "../../assets/no-entries.svg";
import dayjs from "dayjs";
import { ReactComponent as TrophyIcon } from "../../assets/trophy.svg";
import { ReactComponent as StatsIcon } from "../../assets/stats.svg";
import { ReactComponent as MealIcon } from "../../assets/meal.svg";
import { ReactComponent as RecipeIcon } from "../../assets/recipe.svg";
import { selectCurrentUser } from "../../features/auth/authSelectors";
import DiaryTracker from "../Extra/DiaryTracker";
import AddWeightForm from "../Modals/AddWeight";
import {
  copyYesterdayData,
  deleteIngredients,
  pasteMultiSelectData,
  updateIngredients
} from "../../api/diaryApi";
import {
  completeDiaryDay,
  completePreviousDiaryPoints,
  getCurrentUserDiaries,
  getCurrentUserDiaryLevels
} from "../../features/diary/diarySlice";
import {
  selectAllDiariesData,
  selectAllDiaryLevels,
  selectAllDiaryOutOf800g,
  selectAllDiaryOutOfCalories,
  selectAllDiaryOutOfProtein,
  selectDiariesLevelLoading,
  selectDiariesLoading,
  selectShowingLeaderboard
} from "../../features/diary/diarySelectors";
import PickerWithButtonField from "../Extra/DatePicker";
import {
  addMissingDates,
  generateDateArray
} from "../../utils/getAvailableDiaryDates";
import WeekView from "../Extra/WeekView";
import { options800gms, optionsProteins } from "../../utils/mockWeightData";
import LongMenu from "../Extra/ThreeDotMenu";
import useThemeColor from "../../utils/useTheme";
import {
  convertItemQuantityToGrams,
  getTotalWeight
} from "../../utils/weightConvert";
import StatsList from "../Extra/StatsList";
import ThreeDotMenu from "../Extra/ThreeDotMenuForMobile";
import Leaderboard from "../Extra/Leaderboard";
import { formatDate } from "../../utils/formatDate";
import AddMealForLevel3 from "../Extra/AddMealForLevel3";
import AddMealOrRecipeToDiary from "../Extra/AddMealOrRecipeToDiary";
import { useLocation, useNavigate } from "react-router-dom";
import useIsMobile from "../Extra/useMobile";

const pulse = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;

const LoadingScreen = () => {
  return (
    <Box
      sx={{
        width: { md: "95%", lg: "100vw" },
        opacity: 0.6,
        height: "100vh",
        margin: "24px 16px",
        background: "linear-gradient(135deg, #358ad0 0%, #1d6fa5 100%)",
        display: "flex",
        borderRadius: "10px",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "#ffffff"
      }}
    >
      <CircularProgress
        size={80}
        thickness={4.5}
        sx={{
          color: "white",
          mb: 3,
          animation: `${pulse} 1.5s infinite ease-in-out`
        }}
      />
      <Typography
        variant="h5"
        sx={{
          fontWeight: 500,
          animation: `${pulse} 2s infinite ease-in-out`
        }}
      >
        Gathering your food entries...
      </Typography>
    </Box>
  );
};

const ConfirmModal = ({ open, handleClose, handleConfirm, styles }) => {
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: styles.lightGreyColor,
          boxShadow: 24,
          p: 2.2,
          borderRadius: 2,
          minWidth: 240,
          textAlign: "center"
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, color: styles.color }}>
          Are you sure?
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            onClick={handleConfirm}
            sx={{
              p: "8px 14px",
              background: "#368AD0",
              color: "#fcfcfc",
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": {
                background: "#2D74B2"
              }
            }}
          >
            Yes
          </Button>
          <Button
            onClick={handleClose}
            sx={{
              p: "8px 14px",
              color: "#368AD0",
              textTransform: "none",
              fontWeight: "bold",
              backgroundColor: "#ffffff",
              "&:hover": {
                backgroundColor: "#f0f0f0"
              }
            }}
          >
            No
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
const CopyYesterdayButton = ({
  diaryData,
  activeDay,
  selectedLevel,
  currentLevel,
  currentActiveData,
  setSnackbarMessage,
  setOpenSnackbar,
  setIsRefreshNeeded,
  dispatch,
  copyYesterdayData,
  styles,
  onPasteClick,
  onCopyClick
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Check if conditions are met
  const isConditionMet = () => {
    const previousDayData = diaryData?.find(
      (item) =>
        item.currentDayDate ===
        dayjs(activeDay)?.subtract(1, "day")?.format("YYYY-MM-DD")
    );

    const is800gEmpty = previousDayData?.entry800g?.length === 0;
    const isProteinEmpty =
      selectedLevel !== "level1"
        ? previousDayData?.protein?.length === 0
        : true;
    const isOtherFoodEmpty =
      selectedLevel === "level3"
        ? previousDayData?.otherFood?.length === 0
        : true;

    return is800gEmpty && isProteinEmpty && isOtherFoodEmpty;
  };

  const handleClick = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    if (isConditionMet()) {
      setSnackbarMessage("No data for yesterday");
      setOpenSnackbar(true);
      return;
    }
    try {
      const previousDay = dayjs(activeDay)
        ?.subtract(1, "day")
        ?.format("YYYY-MM-DD");
      const previousDayData = diaryData?.find(
        (item) => item.currentDayDate === previousDay
      );
      const tomorrow = dayjs().add(1, "day").startOf("day");

      // Check if the input date is tomorrow
      const isTomorrow = dayjs(activeDay).isSame(tomorrow, "day");

      await copyYesterdayData({
        levelId: currentLevel?.levelId,
        previousDiaryId: previousDayData?._id,
        currentDiaryId: currentActiveData?._id,
        currentDayDate: activeDay,
        completionNotNeeded: isTomorrow
      });

      localStorage.setItem(
        "lastDiaryVisitedLevel",
        JSON.stringify({
          level: currentLevel?.levelName,
          activeDay,
          lastUpdated: dayjs().format("YYYY-MM-DD")
        })
      );

      setIsRefreshNeeded(true);
      setTimeout(() => {
        dispatch(
          getCurrentUserDiaries({ activeDay, levelId: currentLevel?.levelId })
        );
      }, 2000);
    } catch (err) {
      setSnackbarMessage(err?.error || "Error while copying data");
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="10px"
      sx={{
        p: "0px 10px"
      }}
    >
      {/* <IconButton
        onClick={onCopyClick}
        sx={{
          padding: "11px",
          borderRadius: "10px",
          background: styles.lightDiaryTracker
        }}
      >
        <Box
          component="img"
          src="/images/check.png"
          alt="base"
          sx={{
            width: 20,
            height: 20,
            objectFit: "cover"
          }}
        />
      </IconButton> */}
      <Button
        variant="outlined"
        onClick={onCopyClick}
        sx={{
          // fontSize: "small",
          p: "8px 10px",
          fontWeight: "bold",
          // background: isConditionMet() ? "#A7C8E9" : "#368AD0",
          // color: isConditionMet() ? "#64AEDD" : "#fcfcfc",
          cursor: "pointer",
          textTransform: "none"
          // "&:hover": {
          //   background: isConditionMet() ? "#A7C8E9" : "#2D74B2"
          // }
        }}
      >
        {/* <Box
            component="img"
            src="/images/paste.png"
            alt="base"
            sx={{
              mr: 1,
              width: 20,
              height: 20,
              objectFit: "cover"
            }}
          /> */}
        Multi-Select
      </Button>
      {JSON.parse(localStorage.getItem("copyIngredientsData") || "[]")
        ?.length ? (
        <Button
          variant="outlined"
          onClick={onPasteClick}
          sx={{
            // fontSize: "small",
            p: "8px 10px",
            fontWeight: "bold",
            // background: isConditionMet() ? "#A7C8E9" : "#368AD0",
            // color: isConditionMet() ? "#64AEDD" : "#fcfcfc",
            cursor: "pointer",
            textTransform: "none"
            // "&:hover": {
            //   background: isConditionMet() ? "#A7C8E9" : "#2D74B2"
            // }
          }}
        >
          {/* <Box
            component="img"
            src="/images/paste.png"
            alt="base"
            sx={{
              mr: 1,
              width: 20,
              height: 20,
              objectFit: "cover"
            }}
          /> */}
          Paste
        </Button>
      ) : (
        <></>
      )}
      <Button
        onClick={handleClick}
        sx={{
          fontSize: "small",
          p: "8px 10px",
          background: isConditionMet() ? "#A7C8E9" : "#368AD0",
          color: isConditionMet() ? "#64AEDD" : "#fcfcfc",
          cursor: "pointer",
          textTransform: "none",
          "&:hover": {
            background: isConditionMet() ? "#A7C8E9" : "#2D74B2"
          }
        }}
      >
        Copy yesterday
      </Button>
    </Box>
  );
};
const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
  color: "white",
  "&.Mui-checked": {
    color: "#1976d2"
  },
  "& .MuiSvgIcon-root": {
    display: "none" // Hide the default checkmark
  },
  "&::before": {
    content: '""',
    display: "block",
    width: "17px",
    height: "17px",
    border: "2px solid white",
    backgroundColor: "white", // Changed from transparent to white
    boxSizing: "border-box"
  },
  "&.Mui-checked::before": {
    backgroundColor: "#1976d2",
    border: "2px solid #1976d2"
  }
}));

const updatedItemAccordingQuantity = (item) => {
  const details = item.extraDetails;
  if (!details) return;
  const metricServingAmount = parseFloat(
    details?.serving?.metric_serving_amount || item?.noOfQty
  );
  const isCustom = details?.isCustomFood || item?.type === "customFood";
  let customScalingFactor;
  if (isCustom) {
    customScalingFactor = item?.noOfQty
      ? item?.noOfQty
      : +convertItemQuantityToGrams(item).quantity /
        (details ? +convertItemQuantityToGrams(details)?.quantity || 1 : 1);
  }
  const scalingFactor = isCustom
    ? customScalingFactor
    : convertItemQuantityToGrams(item, metricServingAmount).quantity /
      metricServingAmount;
  const excludedFields = [
    "serving_id",
    "serving_description",
    "serving_url",
    "metric_serving_unit"
  ];
  const updatedDetails = Object.entries(details?.serving).reduce(
    (acc, [key, value]) => {
      if (!excludedFields.includes(key) && !isNaN(Number(value))) {
        acc[key] = (value * scalingFactor).toFixed(2);
      }
      return acc;
    },
    {}
  );
  return {
    ...item,
    extraDetails: {
      ...item.extraDetails,
      serving: { ...item.extraDetails.serving, ...updatedDetails }
    }
  };
};
const MobileLevelsDropdown = ({
  allLevels,
  selectedLevel,
  handleLevelSelect,
  userData,
  setSnackbarMessage,
  setOpenSnackbar
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const styles = useThemeColor();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLevelClick = (levelName, levelActive) => {
    if (levelActive) {
      handleLevelSelect(levelName);
    } else {
      if (userData?.isDiarySubscriptionNeeded) {
        setSnackbarMessage("Please purchase subscription to unlock");
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage(
          "Please purchase the corresponding Training Program to unlock"
        );
        setOpenSnackbar(true);
      }
    }
    handleClose();
  };

  // Find current level info
  const currentLevel = allLevels?.find(
    (level) => level.levelName === selectedLevel
  );

  return (
    <Box sx={{ position: "relative" }}>
      <Button
        onClick={handleClick}
        size="small"
        variant="outlined"
        endIcon={<KeyboardArrowDown sx={{ width: 16, height: 16 }} />}
        aria-controls={open ? "levels-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          borderRadius: "8px",
          minWidth: "64px",
          height: "28px",
          padding: "0px 8px",
          borderColor: "rgba(255, 255, 255, 0.2)",
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderColor: "rgba(255, 255, 255, 0.3)"
          },
          color: styles.color,
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)"
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            textTransform: "none",
            letterSpacing: "0.4px"
          }}
        >
          {currentLevel?.shortLabel || "Pillar"}
        </Typography>
      </Button>

      <Menu
        id="levels-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "dropdown-button",
          dense: true,
          sx: {
            py: 0.5,
            minWidth: "100px",
            bgcolor: styles.background || "rgba(18, 18, 18, 0.95)"
          }
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: "10px",
            mt: 0.5,
            border: "1px solid rgba(255, 255, 255, 0.1)",
            overflow: "hidden"
          }
        }}
      >
        {allLevels?.map(({ title, shortLabel, levelName, levelActive }) => (
          <MenuItem
            key={levelName}
            onClick={() => handleLevelClick(levelName, levelActive)}
            selected={selectedLevel === levelName}
            disabled={!levelActive}
            sx={{
              py: 1,
              px: 2,
              display: "flex",
              alignItems: "center",
              border: 0,
              color: styles.color,
              minHeight: "36px",
              justifyContent: "space-between",
              borderRadius: "4px",
              // mx: 0.5,
              // my: 0.25,
              "&.Mui-selected": {
                backgroundColor: "rgba(100, 174, 221, 0.15)",
                "&:hover": {
                  backgroundColor: "rgba(100, 174, 221, 0.25)"
                }
              },
              "&:hover": {
                backgroundColor: levelActive
                  ? "rgba(255, 255, 255, 0.05)"
                  : "transparent"
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: selectedLevel === levelName ? 600 : 500,
                  color: styles.color
                }}
              >
                {title}
              </Typography>
            </Box>
            {!levelActive && (
              <LockIcon
                style={{
                  width: 18,
                  height: 18,
                  marginLeft: 10
                  // color: "rgba(156, 163, 175, 0.7)"
                }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const Diary = ({
  isShowingAllStats,
  setIsShowingAllStats,
  isShowingLeaderBoard,
  setIsShowingLeaderBoard,
  isShowingCreateMeal,
  setIsShowingCreateMeal,
  isShowingCreateRecipe,
  setIsShowingCreateRecipe,
  isShowingCreateCustomFood,
  setIsShowingCreateCustomFood,
  isAddingMealItem,
  setIsAddingMealItem,
  editMealToDiary,
  setEditMealToDiary,
  editRecipeToDiary,
  setEditRecipeToDiary,
  editCustomFoodToDiary,
  setEditCustomFoodToDiary,
  editOneOffToDiary,
  setEditOneOffToDiary,
  setOpenSubscriptionModal,
  activeLevelForMobile,
  setActiveLevelForMobile
}) => {
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = React.useState("");
  // const [isShowingAllStats, setIsShowingAllStats] = useState(false);
  // const [isShowingLeaderBoard, setIsShowingLeaderBoard] = useState(false);
  // const [isShowingCreateMeal, setIsShowingCreateMeal] = useState(false);
  // const [isShowingCreateRecipe, setIsShowingCreateRecipe] = useState(false);
  // const [isShowingCreateCustomFood, setIsShowingCreateCustomFood] =
  //   useState(false);
  // const [isAddingMealItem, setIsAddingMealItem] = useState(false);
  // const [editMealToDiary, setEditMealToDiary] = useState(false);
  // const [editRecipeToDiary, setEditRecipeToDiary] = useState(false);
  // const [editCustomFoodToDiary, setEditCustomFoodToDiary] = useState(false);
  const [selectedOptionData, setSelectedOptionData] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [customFoodFormData, setCustomFoodFormData] = useState({
    name: "",
    quantity: "",
    noOfQty: "",
    unit: "grams",
    type: "800g",
    details: {
      serving: {
        serving_id: String(Math.floor(Math.random() * 90000) + 10000),
        metric_serving_amount: "",
        metric_serving_unit: "g",
        calories: "",
        carbohydrate: "",
        protein: "",
        fat: "",
        saturated_fat: "",
        sodium: "",
        potassium: "",
        fiber: "",
        vitamin_a: "",
        vitamin_c: "",
        vitamin_d: "",
        calcium: "",
        iron: ""
      }
    }
  });
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const leaderboard = searchParams.get("leaderboard") || "";
  const fromPoints = searchParams.get("fromPoints") || false;
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [selectedItems, setSelectedItems] = useState(() => {
    return (
      JSON.parse(localStorage.getItem("copyIngredientsData") || "[]") || []
    );
  });

  const handleCheckboxChange = (id) => {
    setSelectedItems((prev) => {
      let updated = prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id];
      localStorage.setItem("copyIngredientsData", JSON.stringify(updated));
      return updated;
    });
  };
  useEffect(() => {
    localStorage.setItem("lastVisitedRoute", "/diary");
  }, []);
  const dispatch = useDispatch();
  const [openConfirmModal, setOpenConfirmModal] = React.useState(null);
  const handleCloseModal = () => setOpenConfirmModal(null);
  const handleConfirm = async () => {
    await updateIngredients({
      diaryId: currentActiveData?._id,
      levelId: currentLevel?.levelId,
      [openConfirmModal]: [],
      currentDayDate: activeDay
    });
    setIsRefreshNeeded(true);
    dispatch(
      getCurrentUserDiaries({ activeDay, levelId: currentLevel?.levelId })
    );
    // your confirmation logic
    setOpenConfirmModal(null);
  };
  const [openWeightModal, setOpenWeightModal] = useState("");
  const [date, setDate] = useState(dayjs());
  const allLevels = useSelector(selectAllDiaryLevels);
  const diaryLoading = useSelector(selectDiariesLoading);
  const loading = useSelector(selectDiariesLevelLoading);
  const theme = useTheme();
  const isInMobile = useIsMobile();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")) || isInMobile;
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  const isInsideTablet = useMediaQuery(
    "(min-width: 700px) and (max-width: 900px)"
  );
  const visibleDaysCount = isMobile ? 1 : 7; // Number of days to show at a time
  const isLaptop = useMediaQuery("(min-width: 900px) and (max-width: 1400px)");
  const isBiggerLaptop = useMediaQuery(
    "(min-width: 1400px) and (max-width: 1620px)"
  );
  const allDiaryData = useSelector(selectAllDiariesData);
  const total800g = useSelector(selectAllDiaryOutOf800g);
  const totalProteins = useSelector(selectAllDiaryOutOfProtein);
  const totalCalories = useSelector(selectAllDiaryOutOfCalories);
  const userData = useSelector(selectCurrentUser);
  const [diaryData, setDiaryData] = useState([]);
  const [activeDay, setActiveDay] = useState(dayjs().format("YYYY-MM-DD"));
  const [isAddingWeight, setIsAddingWeight] = useState("");
  const [editIngredientData, setEditIngredientData] = useState();
  const [isRefreshNeeded, setIsRefreshNeeded] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const showLeaderBoard = useSelector(selectShowingLeaderboard);
  const styles = useThemeColor();
  const levelData = JSON.parse(
    localStorage.getItem("lastDiaryVisitedLevel") || "{}"
  );
  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    setActiveLevelForMobile(true);
    localStorage.removeItem("copyIngredientsData");
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [isViewingUserMeals, setIsViewingUserMeals] = useState(false);

  const currentLevel = useMemo(() => {
    return allLevels?.find((item) => item?.levelName === selectedLevel);
  }, [allLevels, selectedLevel]);
  useEffect(() => {
    const redirectToMod = localStorage.getItem("redirectToMod");
    if (redirectToMod) navigate("/");
    else {
      localStorage.setItem("diaryLastVisited", dayjs().format("YYYY-MM-DD"));
    }
    localStorage.removeItem("redirectToMod");
    dispatch(getCurrentUserDiaryLevels(dayjs().format("YYYY-MM-DD")));
  }, [dispatch]);

  const isContentLocked =
    // userData?.isDiaryFreeTrialExpired && userData?.isDiarySubscriptionNeeded
    //   ? true
    //   :
    // userData?.isDiaryFreeTrialExpired &&
    // userData?.isDiarySubscriptionNeeded &&
    !currentLevel?.levelActive;

  useEffect(() => {
    if (Object.values(levelData)?.length) {
      setSelectedLevel(levelData?.level);
      setActiveLevelForMobile(true);
    } else if (
      (userData?.activeDiaryLevel && !userData?.isDiarySubscriptionNeeded) ||
      (userData?.activeDiaryLevel &&
        userData?.isDiarySubscriptionNeeded &&
        !userData.isDiaryFreeTrialExpired)
    ) {
      setSelectedLevel(userData?.activeDiaryLevel);
      setActiveLevelForMobile(true);
    }
    // else {
    //   if (Object.values(levelData)?.length) {
    //     setSelectedLevel(levelData?.level);
    //   }
    // }
  }, [userData]);
  useEffect(() => {
    if (currentLevel?.levelId) {
      dispatch(
        getCurrentUserDiaries({
          activeDay,
          levelId: currentLevel?.levelId,
          firstLoad: true
        })
      );
    }
  }, [dispatch, currentLevel?.levelId, activeDay]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    const scrollableElement = document.getElementById("dashboard-main");
    scrollableElement.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (
      !userData?.isFailedPayment &&
      !(
        userData?.isAdmin === 1 ||
        ["Coach", "Company"].includes(userData?.userType)
      ) &&
      !userData?.isSubscriptionActive &&
      (userData?.isTrainingFreeTrialExpired ||
        userData?.isDiaryFreeTrialExpired)
    ) {
      setOpenSubscriptionModal(true);
    }
  }, [userData]);

  // useEffect(() => {
  //   dispatch(
  //     completePreviousDiaryPoints({
  //       levelId: currentLevel?.levelId,
  //       currentDate: dayjs().format("YYYY-MM-DD")
  //     })
  //   );
  // }, [currentLevel]);

  useEffect(() => {
    if (allDiaryData) {
      // if (!isRefreshNeeded) {
      //   const currentUserDay = dayjs().format("YYYY-MM-DD");
      //   const previousDiaryDayData = allDiaryData?.filter((item) => {
      //     const tomorrow = dayjs().add(1, "day").startOf("day");

      //     return (
      //       item.currentDayDate !== currentUserDay &&
      //       !dayjs(item.currentDayDate).isSame(tomorrow, "day") &&
      //       !item?.completionDiaryDayDate &&
      //       (item?.entry800g?.length ||
      //         item?.protein?.length ||
      //         item?.otherFood?.length)
      //     );
      //   });
      //   if (previousDiaryDayData?.length) {
      //     dispatch(
      //       completeDiaryDay({
      //         levelId: currentLevel?.levelId,
      //         diaryIds: previousDiaryDayData?.map((item) => item?._id)
      //       })
      //     );
      //     setTimeout(() => {
      //       dispatch(
      //         getCurrentUserDiaries({
      //           activeDay,
      //           levelId: currentLevel?.levelId
      //         })
      //       );
      //     }, 1000);
      //   }
      // }
      const updatedData = addMissingDates(
        allDiaryData,
        dayjs(userData?.["createdAt"]).startOf("month"),
        currentLevel,
        total800g,
        totalProteins,
        totalCalories
      );
      setDiaryData(updatedData);
    }
  }, [userData, allDiaryData]);

  useEffect(() => {
    if (!isRefreshNeeded) {
      const level = JSON.parse(
        localStorage.getItem("lastDiaryVisitedLevel") || "{}"
      );
      if (Object.values(level)?.length) {
        setActiveDay(level?.activeDay);
      } else {
        setActiveDay(dayjs().format("YYYY-MM-DD"));
      }
    }
  }, [isRefreshNeeded]);
  const [startDay, setStartDay] = useState(1); // Start displaying from day 1

  const days = useMemo(() => {
    return currentLevel?.levelName
      ? generateDateArray(
          dayjs(userData?.["createdAt"]).startOf("month").format("YYYY-MM-DD")
        )
      : [];
  }, [currentLevel]);

  const getVisibleDays = (days, startDay) => {
    if (!days?.length) return [];
    if (isMobile) {
      // For mobile, return only one day
      return days.slice(startDay - 1, startDay);
    }
    const firstDate = dayjs(days[startDay - 1]?.value);
    const dayOfWeek = firstDate.day(); // 0 for Sunday, 6 for Saturday

    if (startDay === 1 && dayOfWeek === 6) {
      // First iteration when month starts with Saturday: Show only Saturday
      return days.slice(0, 1);
    } else {
      // For all other cases
      const startIndex = startDay - 1;
      const remainingDays = days.length - startIndex;
      const daysToShow = Math.min(7 - dayOfWeek, remainingDays);
      return days.slice(startIndex, startIndex + daysToShow);
    }
  };

  // Use it like this:
  const visibleDays = getVisibleDays(days, startDay);
  // const visibleDays = days.slice(startDay - 1, startDay - 1 + visibleDaysCount);
  const [onTodayClicked, setOnTodayClicked] = useState(false);
  // const [visibleDaysCount, setVisibleDaysCount] = useState(1);
  useEffect(() => {
    if (diaryData && !isRefreshNeeded) {
      const createdDate = dayjs(userData?.["createdAt"]).startOf("month");
      const currentDate = dayjs();
      const weekDiff = currentDate.diff(createdDate, "week");
      const levelStored = JSON.parse(
        localStorage.getItem("lastDiaryVisitedLevel") || "{}"
      );
      // Handle case when levelData exists with activeDay
      if (Object.values(levelStored)?.length && levelStored?.activeDay) {
        const activeDayDate = dayjs(levelStored.activeDay);
        if (isMobile) {
          // For mobile, find the index of activeDay in days array
          const activeDayIndex = days.findIndex((day) =>
            dayjs(day.value).isSame(activeDayDate, "day")
          );
          if (activeDayIndex !== -1) {
            setStartDay(activeDayIndex + 1);
          }
        } else {
          // For desktop, find the start of the week containing activeDay
          const activeWeekSunday = activeDayDate.startOf("week");
          const sundayIndex = days.findIndex((day) =>
            dayjs(day.value).isSame(activeWeekSunday, "day")
          );

          if (sundayIndex !== -1) {
            setStartDay(sundayIndex + 1);
          } else {
            // If Sunday not found, find the closest available week
            const activeDayIndex = days.findIndex((day) =>
              dayjs(day.value).isSame(activeDayDate, "day")
            );
            if (activeDayIndex !== -1) {
              // Set to start of the week containing activeDay
              const weekStart = Math.max(
                1,
                Math.floor(activeDayIndex / 7) * 7 + 1
              );
              setStartDay(weekStart);
            }
          }
        }
      } else {
        // Original logic for when no levelData exists
        if (isMobile) {
          const todayIndex = days.findIndex((day) =>
            dayjs(day.value).isSame(currentDate, "day")
          );
          setStartDay(todayIndex !== -1 ? todayIndex + 1 : 1);

          if (todayIndex !== -1) {
            setActiveDay(dayjs(days[todayIndex].value).format("YYYY-MM-DD"));
          }
        } else {
          if (weekDiff >= 1) {
            const currentWeekSunday = currentDate.startOf("week");
            const sundayIndex = days.findIndex((day) =>
              dayjs(day.value).isSame(currentWeekSunday, "day")
            );

            if (sundayIndex !== -1) {
              setStartDay(sundayIndex + 1);
            } else {
              const lastWeekStart = Math.max(1, days.length - 7);
              setStartDay(lastWeekStart);
            }
          } else {
            setStartDay(2);
          }
        }
      }
    }
  }, [days, isMobile, onTodayClicked]);

  const handleDayClick = (day) => {
    setActiveDay(day);
  };

  const currentActiveData = useMemo(() => {
    return diaryData?.find((item) => item.currentDayDate === activeDay);
  }, [diaryData, activeDay]);
  const allCompletedData = useMemo(() => {
    return diaryData
      ?.filter((item) => item.completionDiaryDayDate)
      ?.map((item) => item.currentDayDate);
  }, [diaryData]);

  useEffect(() => {
    setNoteText(currentActiveData?.notes || "");
    setIsAddingNote(false);
  }, [currentActiveData]);

  // const handleNext = () => {
  //   if (startDay + visibleDaysCount - 1 < days.length) {
  //     setStartDay((prev) => prev + visibleDaysCount);
  //   }
  // };

  // const handlePrevious = () => {
  //   if (startDay > 1) {
  //     setStartDay((prev) => prev - visibleDaysCount);
  //   }
  // };
  const handleNext = () => {
    const firstDate = dayjs(days[startDay - 1]?.value);
    const isFirstIteration = startDay === 1;
    if (isMobile) {
      // For mobile, move forward one day at a time
      const nextDay = Math.min(days.length, startDay + 1);
      setStartDay(nextDay);
      const nextDayDate = days[nextDay - 1]?.value;
      setActiveDay(dayjs(nextDayDate).format("YYYY-MM-DD"));
    } else {
      if (isFirstIteration) {
        // After first iteration, jump to next Sunday
        const nextSunday = firstDate.endOf("week").add(1, "day");
        const nextStartIndex = days.findIndex((day) =>
          dayjs(day.value).isSame(nextSunday, "day")
        );
        if (nextStartIndex !== -1) {
          setStartDay(nextStartIndex + 1);
        }
      } else {
        // For subsequent clicks, move forward by 7 days
        setStartDay(startDay + 7);
      }
    }
  };

  const handlePrevious = () => {
    if (isMobile) {
      // For mobile, move back one day at a time
      const prevDay = Math.max(1, startDay - 1);
      setStartDay(prevDay);
      const previousDayDate = days[prevDay - 1]?.value;
      setActiveDay(dayjs(previousDayDate).format("YYYY-MM-DD"));
    } else {
      // Move back by 7 days, but not before the start
      const newStartDay = Math.max(1, startDay - 7);
      setStartDay(newStartDay);
    }
  };

  const summaryData = useCallback(
    (type) => {
      if (selectedLevel !== "level3") {
        return {};
      }
      return currentActiveData?.[type]?.reduce(
        (acc, item) => {
          const updatedItem = updatedItemAccordingQuantity(item);
          acc.metric_serving_amount += parseFloat(
            updatedItem?.extraDetails.serving.metric_serving_amount || 0
          );
          acc.calories += parseFloat(
            updatedItem?.extraDetails.serving.calories || 0
          );
          acc.protein += parseFloat(
            updatedItem?.extraDetails.serving.protein || 0
          );
          acc.carbohydrate += parseFloat(
            updatedItem?.extraDetails.serving.carbohydrate || 0
          );
          acc.fiber += parseFloat(updatedItem?.extraDetails.serving.fiber || 0);
          acc.fat += parseFloat(updatedItem?.extraDetails.serving.fat || 0);
          return acc;
        },
        {
          metric_serving_amount: 0,
          calories: 0,
          protein: 0,
          carbohydrate: 0,
          fiber: 0,
          fat: 0
        }
      );
    },
    [currentActiveData]
  );

  function sumCustomFoodServingAmounts(data) {
    const customFoodServings = [
      ...data.protein.filter(
        (item) =>
          item.extraDetails?.type === "customFood" ||
          item.extraDetails?.isFromSuperAdmin ||
          item.extraDetails?.isCustomFood ||
          item?.type === "customFood"
      ),
      ...data.otherFood.filter(
        (item) =>
          item.extraDetails?.type === "customFood" ||
          item.extraDetails?.isFromSuperAdmin ||
          item.extraDetails?.isCustomFood ||
          item?.type === "customFood"
      )
    ];
    return customFoodServings.reduce((sum, item) => {
      const updatedItem = updatedItemAccordingQuantity(item);

      return (
        sum + (+updatedItem.extraDetails?.serving?.metric_serving_amount || 0)
      );
    }, 0);
  }
  useEffect(() => {
    if (leaderboard) {
      setIsShowingLeaderBoard(true);
    }
  }, [leaderboard]);
  return isAddingWeight ? (
    <AddWeightForm
      diaryId={currentActiveData?._id || ""}
      activeDay={activeDay}
      selectedLevel={currentLevel}
      isAddingWeight={isAddingWeight}
      setIsAddingWeight={setIsAddingWeight}
      levelId={currentLevel?.levelId}
      editData={editIngredientData}
      setIsRefreshNeeded={setIsRefreshNeeded}
      setEditCustomFoodToDiary={setEditCustomFoodToDiary}
      setEditMealToDiary={setEditMealToDiary}
      setEditRecipeToDiary={setEditRecipeToDiary}
    />
  ) : (
    <Box
      pb={10}
      sx={{
        padding: isMobile || isViewingUserMeals ? "0" : "20px 0 40px 32px"
      }}
    >
      {((!activeLevelForMobile && isMobile) || !isMobile) && (
        <Box display="flex" flexDirection={"column"}>
          <Box
            sx={{
              ...(isMobile && {
                width: "100%",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                gap: "10px",
                color: styles.color
              })
            }}
          >
            {isViewingUserMeals ? (
              <></>
            ) : isShowingAllStats ? (
              <Box display="flex" gap="20px" mb={2}>
                <IconButton onClick={() => setIsShowingAllStats(false)}>
                  <ChevronLeft
                    sx={{ height: "35px", width: "35px", fill: styles.color }}
                  />
                </IconButton>
                <Typography
                  fontSize={isMobile ? "28px" : "30px"}
                  fontWeight={500}
                  color={styles.color}
                >
                  Diet Stats
                </Typography>
              </Box>
            ) : isShowingLeaderBoard ? (
              <Box display="flex" gap="20px" mb={2}>
                <IconButton
                  onClick={() => {
                    setIsShowingLeaderBoard(false);
                    if (leaderboard && !fromPoints) {
                      navigate("/profile");
                    }
                  }}
                >
                  <ChevronLeft
                    sx={{ height: "35px", width: "35px", fill: styles.color }}
                  />
                </IconButton>
                <Typography
                  fontSize={isMobile ? "28px" : "30px"}
                  fontWeight={500}
                  color={styles.color}
                >
                  Leaderboard
                </Typography>
              </Box>
            ) : isShowingCreateMeal || editMealToDiary ? (
              <Box display="flex" gap="20px" mb={2}>
                <IconButton
                  onClick={() => {
                    if (selectedOptionData?.serving) {
                      setSelectedOptionData(null);
                    } else if (isAddingMealItem) {
                      setIsAddingMealItem(false);
                    } else if (editMealToDiary) {
                      setEditMealToDiary(false);
                    } else {
                      setIsShowingCreateMeal(false);
                    }
                  }}
                >
                  <ChevronLeft
                    sx={{ height: "35px", width: "35px", fill: styles.color }}
                  />
                </IconButton>
                <Typography
                  fontSize={isMobile ? "28px" : "30px"}
                  fontWeight={500}
                  color={styles.color}
                >
                  {!isShowingCreateMeal
                    ? "My Meals"
                    : `Add Meal ${isAddingMealItem ? "Item" : ""}`}
                </Typography>
              </Box>
            ) : isShowingCreateRecipe || editRecipeToDiary ? (
              <Box display="flex" gap="20px" mb={2}>
                <IconButton
                  onClick={() => {
                    if (selectedOptionData?.serving) {
                      setSelectedOptionData(null);
                    } else if (isAddingMealItem) {
                      setIsAddingMealItem(false);
                    } else if (editRecipeToDiary) {
                      setEditRecipeToDiary(false);
                    } else {
                      setIsShowingCreateRecipe(false);
                    }
                  }}
                >
                  <ChevronLeft
                    sx={{ height: "35px", width: "35px", fill: styles.color }}
                  />
                </IconButton>
                <Typography
                  fontSize={isMobile ? "28px" : "30px"}
                  fontWeight={500}
                  color={styles.color}
                >
                  {!isShowingCreateRecipe
                    ? "My Recipes"
                    : `Add Recipe ${isAddingMealItem ? "Item" : ""}`}
                </Typography>
              </Box>
            ) : isShowingCreateCustomFood && editOneOffToDiary ? (
              <Box display="flex" gap="20px" mb={2}>
                <IconButton
                  onClick={() => {
                    if (selectedOptionData?.serving) {
                      setSelectedOptionData(null);
                    } else if (editOneOffToDiary) {
                      setEditOneOffToDiary(false);
                    }
                    setIsShowingCreateCustomFood(false);
                  }}
                >
                  <ChevronLeft
                    sx={{ height: "35px", width: "35px", fill: styles.color }}
                  />
                </IconButton>
                <Typography
                  fontSize={isMobile ? "28px" : "30px"}
                  fontWeight={500}
                  color={styles.color}
                >
                  One Off
                </Typography>
              </Box>
            ) : isShowingCreateCustomFood || editCustomFoodToDiary ? (
              <Box display="flex" gap="20px" mb={2}>
                <IconButton
                  onClick={() => {
                    if (selectedOptionData?.serving) {
                      setSelectedOptionData(null);
                    } else if (editCustomFoodToDiary) {
                      setEditCustomFoodToDiary(false);
                    } else {
                      setIsShowingCreateCustomFood(false);
                    }
                  }}
                >
                  <ChevronLeft
                    sx={{ height: "35px", width: "35px", fill: styles.color }}
                  />
                </IconButton>
                <Typography
                  fontSize={isMobile ? "28px" : "30px"}
                  fontWeight={500}
                  color={styles.color}
                >
                  My Foods
                </Typography>
              </Box>
            ) : (
              <>
                <Typography
                  fontSize={isMobile ? "28px" : "30px"}
                  fontWeight={600}
                  ml={2}
                  color={styles.color}
                >
                  Diary
                </Typography>
                {/* <Typography
                  textAlign={isMobile ? "center" : "left"}
                  color="#6B7280"
                  fontSize={isMobile ? "15px" : "16px"}
                  fontWeight={isMobile ? 600 : 500}
                >
                  Where you can track your daily grams
                </Typography> */}
              </>
            )}
          </Box>
          {isShowingLeaderBoard ? (
            <Box>
              <Leaderboard
                levelId={currentLevel?.levelId}
                isViewingUserMeals={isViewingUserMeals}
                setIsViewingUserMeals={setIsViewingUserMeals}
                selectedChallengeId={leaderboard}
              />
            </Box>
          ) : editMealToDiary ? (
            <AddMealOrRecipeToDiary
              currentLevel={currentLevel}
              levelId={currentLevel?.levelId}
              activeDay={activeDay}
              setIsShowingCreateMeal={setIsShowingCreateMeal}
              setIsShowingCreateRecipe={setIsShowingCreateRecipe}
              setEditMealToDiary={setEditMealToDiary}
              setIsRefreshNeeded={setIsRefreshNeeded}
              setEditRecipeToDiary={setEditRecipeToDiary}
              type="meal"
            />
          ) : editRecipeToDiary ? (
            <AddMealOrRecipeToDiary
              currentLevel={currentLevel}
              levelId={currentLevel?.levelId}
              setIsRefreshNeeded={setIsRefreshNeeded}
              activeDay={activeDay}
              setIsShowingCreateMeal={setIsShowingCreateMeal}
              setIsShowingCreateRecipe={setIsShowingCreateRecipe}
              setEditMealToDiary={setEditMealToDiary}
              setEditRecipeToDiary={setEditRecipeToDiary}
              type="recipe"
            />
          ) : editCustomFoodToDiary ? (
            <AddMealOrRecipeToDiary
              currentLevel={currentLevel}
              levelId={currentLevel?.levelId}
              setIsRefreshNeeded={setIsRefreshNeeded}
              activeDay={activeDay}
              setIsShowingCreateMeal={setIsShowingCreateMeal}
              setIsShowingCreateRecipe={setIsShowingCreateRecipe}
              setEditMealToDiary={setEditMealToDiary}
              setEditRecipeToDiary={setEditRecipeToDiary}
              setCustomFoodFormData={setCustomFoodFormData}
              setIsShowingCreateCustomFood={setIsShowingCreateCustomFood}
              setEditCustomFoodToDiary={setEditCustomFoodToDiary}
              type="custom food"
            />
          ) : isShowingCreateMeal ? (
            <Box>
              <AddMealForLevel3
                setEditRecipeToDiary={setEditRecipeToDiary}
                activeDay={activeDay}
                currentLevel={currentLevel}
                setIsRefreshNeeded={setIsRefreshNeeded}
                setEditMealToDiary={setEditMealToDiary}
                setIsAddingWeight={setIsAddingWeight}
                setEditCustomFoodToDiary={setEditCustomFoodToDiary}
                type={"meal"}
                setIsShowingCreateMeal={setIsShowingCreateMeal}
                setIsShowingCreateRecipe={setIsShowingCreateRecipe}
                levelId={currentLevel?.levelId}
                isAddingMealItem={isAddingMealItem}
                setIsAddingMealItem={setIsAddingMealItem}
                selectedOptionData={selectedOptionData}
                setSelectedOptionData={setSelectedOptionData}
              />
            </Box>
          ) : isShowingCreateRecipe ? (
            <Box>
              <AddMealForLevel3
                setEditRecipeToDiary={setEditRecipeToDiary}
                activeDay={activeDay}
                currentLevel={currentLevel}
                setIsRefreshNeeded={setIsRefreshNeeded}
                setEditMealToDiary={setEditMealToDiary}
                setIsAddingWeight={setIsAddingWeight}
                setEditCustomFoodToDiary={setEditCustomFoodToDiary}
                setIsShowingCreateRecipe={setIsShowingCreateRecipe}
                setIsShowingCreateMeal={setIsShowingCreateMeal}
                isAddingMealItem={isAddingMealItem}
                setIsAddingMealItem={setIsAddingMealItem}
                selectedOptionData={selectedOptionData}
                setSelectedOptionData={setSelectedOptionData}
                type={"recipe"}
                levelId={currentLevel?.levelId}
              />
            </Box>
          ) : isShowingCreateCustomFood ? (
            <Box>
              <AddMealForLevel3
                setEditRecipeToDiary={setEditRecipeToDiary}
                activeDay={activeDay}
                currentLevel={currentLevel}
                setIsRefreshNeeded={setIsRefreshNeeded}
                setEditMealToDiary={setEditMealToDiary}
                setIsAddingWeight={setIsAddingWeight}
                setEditCustomFoodToDiary={setEditCustomFoodToDiary}
                setIsShowingCreateCustomFood={setIsShowingCreateCustomFood}
                setIsShowingCreateRecipe={setIsShowingCreateRecipe}
                setIsShowingCreateMeal={setIsShowingCreateMeal}
                isAddingMealItem={isAddingMealItem}
                setIsAddingMealItem={setIsAddingMealItem}
                selectedOptionData={selectedOptionData}
                setSelectedOptionData={setSelectedOptionData}
                customFoodFormData={customFoodFormData}
                setCustomFoodFormData={setCustomFoodFormData}
                type={"customFood"}
                editOneOffToDiary={editOneOffToDiary}
                setEditOneOffToDiary={setEditOneOffToDiary}
                levelId={currentLevel?.levelId}
              />
            </Box>
          ) : (
            <Box
              mt={isMobile ? 0 : 3}
              display={"flex"}
              gap={2}
              flexDirection={isLaptop ? "column" : !isTablet ? "row" : "column"}
            >
              <Box
                sx={{
                  width: isInsideTablet
                    ? "70%"
                    : isTablet || isLaptop
                    ? "95%"
                    : !isMobile
                    ? { sm: "30%", md: "37%", lg: "32%" }
                    : "100%",
                  borderRadius: "24px",
                  // ...(!isMobile && {
                  //   height: "calc(100vh - 40px)"
                  // }),
                  margin: isInsideTablet ? "16px 100px" : 2,
                  background: styles.levelBackgrounds,
                  display: "flex",
                  flexDirection: isLaptop ? "row" : "column",
                  padding:
                    isTablet || isMobile
                      ? "20px"
                      : { md: "24px 11px", lg: "24px 16px" },
                  overflowY: "auto",
                  ...((isTablet || isLaptop) && { height: "100%" })
                }}
                gap={4}
              >
                {allLevels?.map(
                  ({ title, description, levelName, levelActive }) => (
                    <Button
                      fullWidth
                      onClick={() => {
                        if (levelActive) {
                          handleLevelSelect(levelName);
                        } else {
                          if (userData?.isDiarySubscriptionNeeded) {
                            setSnackbarMessage(
                              "Please purchase subscription to unlock"
                            );
                            setOpenSnackbar(true);
                            return;
                          } else {
                            setSnackbarMessage(
                              "Please purchase the corresponding Training Program to unlock"
                            );
                            setOpenSnackbar(true);
                            return;
                          }
                        }
                      }}
                      sx={{
                        padding: isBiggerLaptop
                          ? "48px 10px"
                          : isMobile
                          ? 4
                          : isTablet || isLaptop
                          ? "35px 10px"
                          : 6,
                        alignItems: "center",
                        border:
                          selectedLevel === levelName
                            ? "1px solid #64AEDD"
                            : "1px solid #D1D5DB",
                        borderRadius: "16px",
                        backgroundColor:
                          selectedLevel === levelName
                            ? "#368AD0"
                            : // : isMobile
                              // ? styles.lightGreyColor
                              "transparent"
                      }}
                    >
                      <Box
                        width="100%"
                        display="flex"
                        gap="16px"
                        justifyContent="space-between"
                        textTransform="none"
                        alignItems="center"
                      >
                        <Box
                          textAlign={"start"}
                          // sx={{
                          //   ...(isBiggerLaptop && {
                          //     display: "flex",
                          //     flexDirection: "column",
                          //     justifyContent: "center",
                          //     alignItems: "center",
                          //     width: "100%"
                          //   })
                          // }}
                        >
                          <Box>
                            <Typography
                              color={"#FCFCFC"}
                              fontSize={isBiggerLaptop ? "0.85rem" : "0.95rem"}
                              fontWeight={600}
                            >
                              {title} <sup>&#174;</sup>
                            </Typography>
                            <Typography
                              fontSize="15px"
                              fontWeight={300}
                              color={
                                selectedLevel === levelName
                                  ? "#FCFCFC"
                                  : "#D1D5DB"
                              }
                            >
                              {description}
                            </Typography>
                            {/* {
                              // levelName !== "level1" &&
                              !levelActive && (
                                <Typography
                                  mt={1}
                                  fontSize="12px"
                                  fontWeight={500}
                                  color={
                                    selectedLevel === levelName
                                      ? "#FCFCFC"
                                      : "#D1D5DB"
                                  }
                                >
                                  {`Purchase the ${title} Program to unlock ${title}
                                Logging`}
                                </Typography>
                              )
                            } */}
                          </Box>
                        </Box>
                        <Box>{levelActive ? <></> : <LockIcon />}</Box>
                      </Box>
                    </Button>
                  )
                )}
              </Box>
              {!isMobile && selectedLevel ? (
                loading ? (
                  // add attractive loading component use MUI please
                  <LoadingScreen />
                ) : isShowingAllStats ? (
                  <Box
                    display="flex"
                    sx={{
                      width:
                        isTablet || isLaptop
                          ? "100%"
                          : !isMobile
                          ? { sm: "60%", md: "60%", lg: "75%" }
                          : "100%",
                      padding: "0 25px"
                      // ...((!isMobile || !isTablet || !isLaptop) && {
                      //   maxHeight: "calc(100vh - 40px)"
                      // })
                    }}
                    flexDirection="column"
                    gap={3}
                    ml={isMobile ? 0 : 2}
                  >
                    <StatsList
                      completedDates={allCompletedData}
                      level={currentLevel}
                    />
                  </Box>
                ) : isContentLocked ? (
                  <Box
                    sx={{
                      background:
                        "radial-gradient(circle at 50% 50%, #368AD0, #2D5F87, #1A3A5C)",
                      width: isTablet || isLaptop ? "95%" : { lg: "80%" },
                      ...((isTablet || isLaptop) && {
                        height: "240px"
                      }),
                      margin: "24px 16px",
                      zIndex: 1,
                      borderRadius: "8px"
                    }}
                  >
                    <Box
                      display="flex"
                      borderRadius="8px"
                      justifyContent="center"
                      alignItems="center"
                      mr={1}
                      sx={{
                        width: "100%",
                        height: "100%",
                        backdropFilter: "blur(8px)" // blur effect
                      }}
                    >
                      <LockIcon
                        style={{ color: "#FCFCFC", height: 70, width: 70 }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      width:
                        isTablet || isLaptop
                          ? "100%"
                          : !isMobile
                          ? { sm: "60%", md: "60%", lg: "75%" }
                          : "100%",
                      padding: "0 25px"
                      // ...((!isMobile || !isTablet) && {
                      //   maxHeight: "calc(100vh - 40px)"
                      // })
                    }}
                    flexDirection="column"
                    gap={3}
                    ml={isMobile ? 0 : 2}
                  >
                    <Box
                      display="flex"
                      alignItems={"center"}
                      gap={isTablet ? 2 : 0}
                      justifyContent={isTablet ? "space-between" : "flex-end"}
                    >
                      {isTablet ? (
                        <Box>
                          <MobileLevelsDropdown
                            allLevels={allLevels}
                            selectedLevel={selectedLevel}
                            handleLevelSelect={handleLevelSelect}
                            setActiveLevelForMobile={setActiveLevelForMobile}
                            userData={userData}
                            setSnackbarMessage={setSnackbarMessage}
                            setOpenSnackbar={setOpenSnackbar}
                          />
                        </Box>
                      ) : (
                        <></>
                      )}
                      <Box display="flex" gap="10px">
                        <Button
                          onClick={() => {
                            setActiveDay(dayjs().format("YYYY-MM-DD"));
                            setOnTodayClicked(!onTodayClicked);
                          }}
                          sx={{
                            background: "#368AD0",
                            fontWeight: 600,
                            fontSize: "12px",
                            color: "#FCFCFC",
                            cursor: "pointer",
                            borderRadius: 5,
                            textTransform: "none",
                            paddingX: 2,
                            "&:hover": {
                              background: "#2D74B2"
                            }
                          }}
                        >
                          Jump to Today
                        </Button>
                        <IconButton
                          sx={{
                            padding: "12px",
                            borderRadius: "10px",
                            background: styles.lightDiaryTracker
                          }}
                          onClick={() => {
                            setIsShowingAllStats(true);
                          }}
                        >
                          <StatsIcon
                            style={{
                              width: 16,
                              height: 16
                            }}
                          />
                        </IconButton>
                        {currentLevel?.showLeaderboard ? (
                          <IconButton
                            onClick={() => {
                              setIsShowingLeaderBoard(true);
                              navigate("/diary", { replace: true });
                            }}
                            sx={{
                              padding: "12px",
                              borderRadius: "10px",
                              background: styles.lightDiaryTracker
                            }}
                          >
                            <TrophyIcon
                              style={{
                                width: 16,
                                height: 16
                              }}
                            />
                          </IconButton>
                        ) : (
                          <></>
                        )}
                        {currentLevel?.levelName === "level3" && (
                          <>
                            {" "}
                            <IconButton
                              sx={{
                                padding: "12px",
                                borderRadius: "10px",
                                background: styles.lightDiaryTracker
                              }}
                              onClick={() => {
                                setEditMealToDiary(true);
                                // dispatch(updateAddingMeal({ meal: {} }));
                              }}
                            >
                              <MealIcon
                                style={{
                                  width: 16,
                                  height: 16
                                }}
                              />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setEditRecipeToDiary(true);
                                // setIsShowingCreateRecipe(true);
                                // dispatch(updateAddingRecipe({ recipe: {} }));
                              }}
                              sx={{
                                padding: "12px",
                                borderRadius: "10px",
                                background: styles.lightDiaryTracker
                              }}
                            >
                              <RecipeIcon
                                style={{
                                  width: 16,
                                  height: 16
                                }}
                              />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setIsShowingCreateCustomFood(true);
                                setEditOneOffToDiary(true);
                              }}
                              sx={{
                                padding: "12px",
                                borderRadius: "10px",
                                background: styles.lightDiaryTracker
                              }}
                            >
                              <Fastfood
                                style={{
                                  width: 19,
                                  height: 19,
                                  color: "wheat"
                                }}
                              />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setEditOneOffToDiary(false);
                                setEditCustomFoodToDiary(true);
                              }}
                              sx={{
                                padding: "12px",
                                borderRadius: "10px",
                                background: styles.lightDiaryTracker
                              }}
                            >
                              <Box
                                component="img"
                                src="/images/custom.png"
                                alt="base"
                                sx={{
                                  height: 16,
                                  width: 16,
                                  objectFit: "cover"
                                }}
                              />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      gap="10px"
                      flexDirection="column"
                      // justifyContent="space-between"
                      width="100%"
                    >
                      <Box flexGrow={1} display="flex" justifyContent="center">
                        <PickerWithButtonField
                          maxDate={dayjs(userData?.["createdAt"])
                            .startOf("month")
                            .format("YYYY-MM-DD")}
                          days={days}
                          allCompletedDates={allCompletedData}
                          date={date}
                          visibleDaysCount={visibleDaysCount}
                          setOnTodayClicked={setOnTodayClicked}
                          setStartDay={setStartDay}
                          setDate={setDate}
                          activeDay={activeDay}
                          setActiveDay={setActiveDay}
                          userData={userData}
                          levelData={levelData}
                        />
                      </Box>{" "}
                      <WeekView
                        allCompletedData={allCompletedData}
                        activeDay={activeDay}
                        setActiveDay={setActiveDay}
                        startDay={startDay}
                        visibleDays={visibleDays}
                        visibleDaysCount={visibleDaysCount}
                        days={days}
                        setStartDay={setStartDay}
                        diaryData={diaryData}
                        handleDayClick={handleDayClick}
                        handleNext={handleNext}
                        handlePrevious={handlePrevious}
                      />{" "}
                    </Box>

                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={"20px"}
                      sx={
                        {
                          // ...((!isMobile || !isTablet) && {
                          //   overflowY: "auto",
                          //   maxHeight: "calc(100vh - 120px)"
                          // })
                        }
                      }
                    >
                      <Box display="flex" justifyContent="space-between">
                        <Typography
                          color={styles.color}
                          fontWeight={600}
                          fontSize={"24px"}
                        >
                          {formatDate(activeDay, false)} (
                          {dayjs(activeDay).format(isMobile ? "ddd" : "dddd")})
                        </Typography>
                        <CopyYesterdayButton
                          diaryData={diaryData}
                          styles={styles}
                          activeDay={activeDay}
                          selectedLevel={selectedLevel}
                          currentLevel={currentLevel}
                          currentActiveData={currentActiveData}
                          setSnackbarMessage={setSnackbarMessage}
                          setOpenSnackbar={setOpenSnackbar}
                          setIsRefreshNeeded={setIsRefreshNeeded}
                          dispatch={dispatch}
                          copyYesterdayData={copyYesterdayData}
                          onPasteClick={async () => {
                            try {
                              await pasteMultiSelectData({
                                currentDayDate: activeDay,
                                levelId: currentLevel?.levelId,
                                selectedIngredientIds: selectedItems,
                                outOf800g: currentActiveData?.outOf800g,
                                outOfProtein: currentActiveData?.outOfProtein,
                                outOfCalories: currentActiveData?.outOfCalories,
                                currentDiaryId: currentActiveData?._id
                              });
                              setShowCheckboxes(false);
                              localStorage.removeItem("copyIngredientsData");
                              setSelectedItems([]);
                              setIsRefreshNeeded(true);
                              dispatch(
                                getCurrentUserDiaries({
                                  activeDay,
                                  levelId: currentLevel?.levelId
                                })
                              );
                              localStorage.setItem(
                                "lastDiaryVisitedLevel",
                                JSON.stringify({
                                  activeDay: activeDay,
                                  lastUpdated: activeDay,
                                  level: currentLevel?.levelName,
                                  unit: "grams"
                                })
                              );
                            } catch (err) {
                              setSnackbarMessage(err?.error);
                              setOpenSnackbar(true);
                            }
                          }}
                          onCopyClick={() => {
                            setShowCheckboxes((prev) => {
                              if (prev) {
                                localStorage.removeItem("copyIngredientsData");
                                setSelectedItems([]);
                              }
                              return !prev;
                            });
                          }}
                        />
                      </Box>
                      <Box
                        gap={
                          (isMobile || isTablet) &&
                          currentLevel?.levelName === "level3"
                            ? "20px"
                            : "40px"
                        }
                        display="flex"
                        borderRadius={"16px"}
                        sx={{ background: styles.diaryTracker }}
                        justifyContent="center"
                      >
                        {diaryLoading || !currentLevel?.levelName ? (
                          <CircularProgress sx={{ my: 12 }} size={70} />
                        ) : (
                          <>
                            <DiaryTracker
                              loading={diaryLoading}
                              levelName={currentLevel?.levelName}
                              openWeightModal={openWeightModal}
                              setOpenWeightModal={setOpenWeightModal}
                              title={"#800gChallenge"}
                              levelId={currentLevel?.levelId}
                              diaryId={currentActiveData?._id || ""}
                              value={
                                selectedLevel === "level3"
                                  ? Math.round(
                                      (summaryData("entry800g")
                                        ?.metric_serving_amount || 0) +
                                        sumCustomFoodServingAmounts(
                                          currentActiveData
                                        )
                                    )
                                  : getTotalWeight(
                                      currentActiveData?.entry800g
                                    ) || 0
                              }
                              strokeColor={"#F6541C"}
                              maxValue={currentActiveData?.outOf800g}
                              setIsRefreshNeeded={setIsRefreshNeeded}
                              dayName={activeDay}
                              heightX={
                                currentLevel?.levelName === "level2" ? 210 : 250
                              }
                              widthX={
                                currentLevel?.levelName === "level2" ? 300 : 350
                              }
                            />
                            {currentLevel?.levelName !== "level1" && (
                              <DiaryTracker
                                loading={diaryLoading}
                                levelName={currentLevel?.levelName}
                                openWeightModal={openWeightModal}
                                setOpenWeightModal={setOpenWeightModal}
                                title={"Protein"}
                                levelId={currentLevel?.levelId}
                                diaryId={currentActiveData?._id || ""}
                                value={
                                  selectedLevel === "level3"
                                    ? Math.round(
                                        summaryData("entry800g")?.protein +
                                          summaryData("protein")?.protein +
                                          summaryData("otherFood")?.protein
                                      ) || 0
                                    : getTotalWeight(
                                        currentActiveData?.protein
                                      ) || 0
                                }
                                strokeColor={"#368AD0"}
                                maxValue={currentActiveData?.outOfProtein}
                                setIsRefreshNeeded={setIsRefreshNeeded}
                                dayName={activeDay}
                                heightX={
                                  currentLevel?.levelName === "level2"
                                    ? 210
                                    : 250
                                }
                                widthX={
                                  currentLevel?.levelName === "level2"
                                    ? 300
                                    : 350
                                }
                              />
                            )}
                            {currentLevel?.levelName === "level3" && (
                              <DiaryTracker
                                loading={diaryLoading}
                                isCalories
                                levelName={currentLevel?.levelName}
                                openWeightModal={openWeightModal}
                                setOpenWeightModal={setOpenWeightModal}
                                title={"Calories"}
                                levelId={currentLevel?.levelId}
                                diaryId={currentActiveData?._id || ""}
                                value={
                                  Math.round(
                                    summaryData("entry800g")?.calories +
                                      summaryData("protein")?.calories +
                                      summaryData("otherFood")?.calories
                                  ) || 0
                                }
                                strokeColor={"#FF9431"}
                                maxValue={currentActiveData?.outOfCalories}
                                setIsRefreshNeeded={setIsRefreshNeeded}
                                dayName={activeDay}
                                heightX={
                                  currentLevel?.levelName === "level2"
                                    ? 210
                                    : 250
                                }
                                widthX={
                                  currentLevel?.levelName === "level2"
                                    ? 300
                                    : 350
                                }
                              />
                            )}
                          </>
                        )}
                      </Box>
                      {currentLevel?.levelName === "level3" && (
                        <Box
                          display="flex"
                          mb={1.5}
                          gap="40px"
                          justifyContent="center"
                        >
                          <Button
                            onClick={handleClick}
                            sx={{
                              textTransform: "none",
                              background: "#368AD0",
                              color: "#fcfcfc",
                              height: "40px",
                              fontWeight: 600,
                              width: { lg: "25%", md: "40%", xs: "100%" },
                              padding: "12px 24px",
                              gap: "8px",
                              borderRadius: "4px"
                            }}
                          >
                            <Add /> Add
                          </Button>
                          <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            slotProps={{
                              paper: {
                                style: {
                                  background: styles.background,
                                  color: styles.color
                                }
                              }
                            }}
                            MenuListProps={{
                              "aria-labelledby": "basic-button"
                            }}
                            sx={{
                              "& .MuiPaper-root": {
                                width: anchorEl ? anchorEl.offsetWidth : "auto",
                                background: "#fff",
                                color: "#000"
                                // margin: "0 20px"
                              }
                            }}
                          >
                            <MenuItem
                              sx={{
                                // justifyContent: "center",
                                gap: "10px"
                                // alignItems: "center"
                              }}
                              onClick={() => {
                                handleClose();
                                setEditIngredientData();
                                setIsAddingWeight("entry800g");
                              }}
                            >
                              <Add
                                style={{ marginRight: "4px" }}
                                height={17}
                                width={17}
                              />{" "}
                              <Typography fontSize={14}>Add 800g </Typography>
                            </MenuItem>
                            <MenuItem
                              sx={{
                                gap: "10px"
                                // alignItems: "center",
                                // justifyContent: "center"
                              }}
                              onClick={() => {
                                handleClose();
                                setEditIngredientData();
                                setIsAddingWeight("protein");
                              }}
                            >
                              {" "}
                              <Add
                                style={{ marginRight: "4px" }}
                                height={17}
                                width={17}
                              />
                              <Typography fontSize={14}>Add Protein</Typography>
                            </MenuItem>
                            <MenuItem
                              sx={{
                                gap: "10px"
                                // alignItems: "center",
                                // justifyContent: "center"
                              }}
                              onClick={() => {
                                handleClose();
                                setEditIngredientData();
                                setIsAddingWeight("otherFood");
                              }}
                            >
                              <Add
                                style={{ marginRight: "4px" }}
                                height={17}
                                width={17}
                              />{" "}
                              <Typography fontSize={14}>Add Other</Typography>
                            </MenuItem>
                            <MenuItem
                              sx={{
                                gap: "10px"
                              }}
                              onClick={() => {
                                handleClose();
                                setIsShowingCreateCustomFood(false);
                                setEditOneOffToDiary(false);
                                setEditCustomFoodToDiary(true);
                              }}
                            >
                              <Box
                                component="img"
                                src="/images/custom.png"
                                alt="base"
                                sx={{
                                  height: 20,
                                  width: 20,
                                  objectFit: "cover",
                                  marginRight: "8px"
                                }}
                              />
                              {/* <Add style={{ marginRight: "4px" }} />{" "} */}
                              <Typography fontSize={14}>
                                Add Custom Food
                              </Typography>
                            </MenuItem>
                            <MenuItem
                              sx={{
                                gap: "10px"
                              }}
                              onClick={() => {
                                handleClose();
                                setIsShowingCreateCustomFood(true);
                                setEditOneOffToDiary(true);
                              }}
                            >
                              <Fastfood
                                style={{
                                  marginRight: "4px",
                                  height: 22,
                                  width: 22,
                                  color: "wheat"
                                }}
                                height={14}
                                width={14}
                              />{" "}
                              <Typography fontSize={14}>Add One Off</Typography>
                            </MenuItem>
                            <MenuItem
                              sx={{
                                gap: "10px",
                                alignItems: "center"
                              }}
                              onClick={() => {
                                handleClose();
                                // Action for Add Meal
                                setIsShowingCreateMeal(false);
                                setEditMealToDiary(true);
                              }}
                            >
                              <MealIcon
                                style={{ marginRight: "8px" }}
                                height={20}
                                width={20}
                              />
                              <Typography fontSize={14}>Add Meal</Typography>
                            </MenuItem>
                            <MenuItem
                              sx={{
                                gap: "10px",
                                alignItems: "center"
                              }}
                              onClick={() => {
                                handleClose();
                                // Action for Add Recipe
                                setIsShowingCreateRecipe(false);
                                setEditRecipeToDiary(true);
                              }}
                            >
                              <RecipeIcon
                                style={{ marginRight: "8px" }}
                                height={17}
                                width={17}
                              />
                              <Typography fontSize={14}>Add Recipe</Typography>
                            </MenuItem>
                          </Menu>
                        </Box>
                      )}
                      {["level1", "level2"]?.includes(selectedLevel) ? (
                        <Box
                          width={"100%"}
                          gap="40px"
                          display="flex"
                          justifyContent="center"
                        >
                          <Box display="flex" gap="40px">
                            <Button
                              onClick={() => {
                                setEditIngredientData();
                                setIsAddingWeight("entry800g");
                              }}
                              sx={{
                                textTransform: "none",
                                background: "#368AD0",
                                color: "#fcfcfc",
                                height: "40px",
                                fontWeight: 600,
                                padding: "12px 24px",
                                gap: "8px",
                                borderRadius: "4px"
                              }}
                            >
                              <Add /> Add{" "}
                              {currentLevel?.levelName &&
                              currentLevel?.levelName !== "level1"
                                ? `800g`
                                : ""}
                            </Button>
                            {currentLevel?.levelName &&
                            currentLevel?.levelName !== "level1" ? (
                              <Button
                                onClick={() => {
                                  setEditIngredientData();
                                  setIsAddingWeight("protein");
                                }}
                                sx={{
                                  textTransform: "none",
                                  background: "#368AD0",
                                  color: "#fcfcfc",
                                  height: "40px",
                                  fontWeight: 600,
                                  padding: "12px 24px",
                                  gap: "8px",
                                  borderRadius: "4px"
                                }}
                              >
                                <Add /> Add Protein
                              </Button>
                            ) : (
                              <></>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <></>
                      )}
                      {currentActiveData?.entry800g?.length ? (
                        selectedLevel === "level3" ? (
                          <Box
                            display={"flex"}
                            flexDirection={"column"}
                            gap={2}
                          >
                            <Box
                              mt={-2}
                              display={"flex"}
                              gap={0.4}
                              borderRadius="8px"
                              padding={"12px"}
                              width={"100%"}
                              sx={{ background: styles.lightGreyColor }}
                            >
                              <Box
                                flexDirection="column"
                                display="flex"
                                justifyContent="space-between"
                                width="100%"
                              >
                                <Box
                                  alignItems="center"
                                  display="flex"
                                  justifyContent="space-between"
                                  width="100%"
                                >
                                  <Typography fontWeight="600" color="#f6521c">
                                    {"800g"}
                                  </Typography>
                                  <IconButton
                                    onClick={() => {
                                      handleClose();
                                      setEditIngredientData();
                                      setIsAddingWeight("entry800g");
                                    }}
                                  >
                                    <Add sx={{ color: "#f6521c" }} />
                                  </IconButton>
                                </Box>
                                <Typography fontSize={13}>
                                  {`${
                                    Math.round(
                                      +summaryData("entry800g")?.calories
                                    ) || 0
                                  } Cals · ${
                                    Math.round(
                                      +summaryData("entry800g")?.protein
                                    ) || 0
                                  }g Protein · ${
                                    Math.round(
                                      +summaryData("entry800g")?.carbohydrate
                                    ) || 0
                                  }g Carbs · ${
                                    Math.round(
                                      +summaryData("entry800g")?.fiber
                                    ) || 0
                                  }g Fiber · ${
                                    Math.round(
                                      +summaryData("entry800g")?.fat
                                    ) || 0
                                  }g Fat`}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              // height={"200px"}
                              gap="10px"
                              mb={2}
                              borderRadius="16px"
                              p="10px"
                              alignItems="center"
                              display="flex"
                              flexDirection="column"
                              sx={{ background: styles.lightDiaryTracker }}
                              justifyContent="center"
                            >
                              <Box
                                width={"100%"}
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Box />
                                <Typography
                                  mr={4}
                                  fontSize={"12px"}
                                  color="#8E8E8E"
                                >
                                  (Gram weight)
                                </Typography>
                              </Box>
                              {currentActiveData?.entry800g?.map((item) => (
                                <Box
                                  width={"100%"}
                                  display="flex"
                                  justifyContent="space-between"
                                  key={item.key}
                                >
                                  <Box display={"flex"} alignItems={"center"}>
                                    {showCheckboxes && (
                                      <CustomCheckbox
                                        checked={selectedItems.includes(
                                          item._id
                                        )}
                                        onChange={() =>
                                          handleCheckboxChange(item._id)
                                        }
                                      />
                                    )}
                                    <Box>
                                      <Box
                                        display={"flex"}
                                        alignItems="center"
                                        gap="10px"
                                      >
                                        <Tooltip
                                          title={
                                            options800gms?.find(
                                              (option) =>
                                                option.value === item.name
                                            )?.label ?? item.name
                                          }
                                        >
                                          <Typography
                                            sx={{
                                              cursor: "pointer",
                                              display: "block", // Ensures the container behaves as a block element
                                              maxWidth: isMobile
                                                ? "90px"
                                                : "500px", // Set your fixed width (adjust as needed)
                                              overflow: "hidden", // Hide overflow content
                                              textOverflow: "ellipsis", // Show ellipsis when text overflows
                                              whiteSpace: "nowrap" // Prevent text from wrapping
                                            }}
                                            fontSize={13}
                                            color={styles.color}
                                            textTransform={"capitalize"}
                                          >
                                            {options800gms?.find(
                                              (option) =>
                                                option.value === item.name
                                            )?.label ?? item.name}
                                          </Typography>
                                        </Tooltip>
                                        {item?.type === "customFood" ||
                                        item?.extraDetails?.isCustomFood ? (
                                          item?.extraDetails?.isFromOneOff ? (
                                            <Fastfood
                                              style={{
                                                color: "wheat",
                                                height: 16,
                                                width: 16
                                              }}
                                            />
                                          ) : (
                                            <Box
                                              component="img"
                                              src="/images/custom.png"
                                              alt="base"
                                              sx={{
                                                height: 16,
                                                width: 16,
                                                objectFit: "cover"
                                              }}
                                            />
                                          )
                                        ) : (
                                          <></>
                                        )}
                                        {item?.type === "meal" ? (
                                          <MealIcon
                                            style={{
                                              width: 16,
                                              height: 16
                                            }}
                                          />
                                        ) : item?.type === "recipe" ? (
                                          <RecipeIcon
                                            style={{
                                              width: 16,
                                              height: 16
                                            }}
                                          />
                                        ) : (
                                          <></>
                                        )}
                                      </Box>
                                      <Typography
                                        fontSize={10}
                                        color={styles.disabledColor}
                                      >
                                        {item.time}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box display="flex" alignItems={"center"}>
                                    <Typography fontSize={13} fontWeight={600}>
                                      {
                                        convertItemQuantityToGrams(item)
                                          ?.quantity
                                      }
                                      g
                                    </Typography>
                                    <LongMenu
                                      onClickHandler={async (type) => {
                                        if (type === "edit") {
                                          setEditIngredientData(item);
                                          setIsAddingWeight("entry800g");
                                        } else {
                                          await deleteIngredients(
                                            currentLevel?.levelId,
                                            currentActiveData?._id,
                                            item?._id,
                                            activeDay
                                          );
                                          setIsRefreshNeeded(true);
                                          dispatch(
                                            getCurrentUserDiaries({
                                              activeDay,
                                              levelId: currentLevel?.levelId
                                            })
                                          );
                                        }
                                      }}
                                    />
                                  </Box>
                                </Box>
                              ))}
                              <Button
                                onClick={async () => {
                                  setOpenConfirmModal("entry800g");
                                  // await updateIngredients({
                                  //   diaryId: currentActiveData?._id,
                                  //   levelId: currentLevel?.levelId,
                                  //   entry800g: [],
                                  //   currentDayDate: activeDay
                                  // });
                                  // setIsRefreshNeeded(true);
                                  // dispatch(
                                  //   getCurrentUserDiaries({ activeDay,
                                  //     levelId: currentLevel?.levelId
                                  //   })
                                  // );
                                }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                  p: "2px 8px",
                                  color: styles.color,
                                  border: `1px solid ${styles.color}`,
                                  cursor: "pointer",
                                  textTransform: "none"
                                }}
                              >
                                {/* <IconButton
                                  sx={{
                                    color: styles.textColorDiary,
                                    height: "17px",
                                    width: "17px",
                                    padding: 0,
                                    ":disabled": {
                                      color: styles.textColorDiary
                                    }
                                  }}
                                  disabled
                                >
                                  <Add
                                    color={styles.textColorDiary}
                                    fontSize="12px"
                                  />
                                </IconButton> */}
                                <Typography
                                  color={styles.textColorDiary}
                                  fontSize="13px"
                                >
                                  Clear all
                                </Typography>
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            // height={"200px"}
                            gap="10px"
                            borderRadius="16px"
                            p="20px"
                            alignItems="center"
                            display="flex"
                            flexDirection="column"
                            sx={{ background: styles.lightDiaryTracker }}
                            justifyContent="center"
                          >
                            <Typography
                              width={"100%"}
                              display={"flex"}
                              justifyContent={"flex-start"}
                              fontWeight={600}
                              color="#f6521c"
                            >
                              800g{" "}
                            </Typography>
                            {currentActiveData?.entry800g?.map((item) => (
                              <Box
                                width={"100%"}
                                display="flex"
                                justifyContent="space-between"
                                key={item.key}
                              >
                                <Box display={"flex"} alignItems={"center"}>
                                  {showCheckboxes && (
                                    <CustomCheckbox
                                      checked={selectedItems.includes(item._id)}
                                      onChange={() =>
                                        handleCheckboxChange(item._id)
                                      }
                                    />
                                  )}
                                  <Box>
                                    <Typography
                                      fontSize={13}
                                      color={styles.color}
                                      textTransform={"capitalize"}
                                    >
                                      {options800gms?.find(
                                        (option) => option.value === item.name
                                      )?.label ?? item.name}
                                    </Typography>
                                    <Typography
                                      fontSize={10}
                                      color={styles.disabledColor}
                                    >
                                      {item.time}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box display="flex" alignItems={"center"}>
                                  <Typography fontSize={13} fontWeight={600}>
                                    {convertItemQuantityToGrams(item)?.quantity}
                                    g
                                  </Typography>
                                  <LongMenu
                                    onClickHandler={async (type) => {
                                      if (type === "edit") {
                                        setEditIngredientData(item);
                                        setIsAddingWeight("entry800g");
                                      } else {
                                        await deleteIngredients(
                                          currentLevel?.levelId,
                                          currentActiveData?._id,
                                          item?._id,
                                          activeDay
                                        );
                                        setIsRefreshNeeded(true);
                                        dispatch(
                                          getCurrentUserDiaries({
                                            activeDay,
                                            levelId: currentLevel?.levelId
                                          })
                                        );
                                      }
                                    }}
                                  />
                                </Box>
                              </Box>
                            ))}
                            <Button
                              onClick={async () => {
                                setOpenConfirmModal("entry800g");
                                // await updateIngredients({
                                //   diaryId: currentActiveData?._id,
                                //   levelId: currentLevel?.levelId,
                                //   entry800g: [],
                                //   currentDayDate: activeDay
                                // });
                                // setIsRefreshNeeded(true);
                                // dispatch(
                                //   getCurrentUserDiaries({ activeDay,
                                //     levelId: currentLevel?.levelId
                                //   })
                                // );
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                p: "2px 8px",
                                color: styles.color,
                                border: `1px solid ${styles.color}`,
                                cursor: "pointer",
                                textTransform: "none"
                              }}
                            >
                              {/* <IconButton
                                sx={{
                                  color: styles.textColorDiary,
                                  height: "17px",
                                  width: "17px",
                                  padding: 0,
                                  ":disabled": {
                                    color: styles.textColorDiary
                                  }
                                }}
                                disabled
                              >
                                <Add
                                  color={styles.textColorDiary}
                                  fontSize="12px"
                                />
                              </IconButton> */}
                              <Typography
                                color={styles.textColorDiary}
                                fontSize="13px"
                              >
                                Clear all
                              </Typography>
                            </Button>
                          </Box>
                        )
                      ) : (
                        <Box
                          gap="10px"
                          borderRadius="16px"
                          p="20px"
                          mt={-1}
                          alignItems="center"
                          display="flex"
                          flexDirection="column"
                          sx={{ background: styles.lightDiaryTracker }}
                          justifyContent="center"
                        >
                          <Box
                            display="flex"
                            width={"100%"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                          >
                            <Typography
                              width={"100%"}
                              display={"flex"}
                              justifyContent={"flex-start"}
                              fontWeight={600}
                              color="#f6521c"
                            >
                              800g{" "}
                            </Typography>
                            {selectedLevel === "level3" && (
                              <IconButton
                                onClick={() => {
                                  handleClose();
                                  setEditIngredientData();
                                  setIsAddingWeight("entry800g");
                                }}
                              >
                                <Add sx={{ color: "#f6521c" }} />
                              </IconButton>
                            )}
                          </Box>
                          <NoEntry />
                          <Typography color="#6B7280">No entry</Typography>
                        </Box>
                      )}
                      {currentLevel?.levelName !== "level1" && (
                        <>
                          {currentActiveData?.protein?.length ? (
                            selectedLevel === "level3" ? (
                              <Box
                                display={"flex"}
                                flexDirection={"column"}
                                gap={2}
                              >
                                <Box
                                  display={"flex"}
                                  gap={0.4}
                                  // flexDirection="column"
                                  borderRadius="8px"
                                  padding={"12px"}
                                  width={"100%"}
                                  sx={{ background: styles.lightGreyColor }}
                                >
                                  <Box
                                    flexDirection="column"
                                    display="flex"
                                    justifyContent="space-between"
                                    width="100%"
                                  >
                                    <Box
                                      alignItems="center"
                                      display="flex"
                                      justifyContent="space-between"
                                      width="100%"
                                    >
                                      <Typography
                                        color="#368AD0"
                                        fontWeight="600"
                                      >
                                        {"Protein"}
                                      </Typography>{" "}
                                      <IconButton
                                        onClick={() => {
                                          handleClose();
                                          setEditIngredientData();
                                          setIsAddingWeight("protein");
                                        }}
                                      >
                                        <Add sx={{ color: "#368AD0" }} />
                                      </IconButton>
                                    </Box>
                                    <Typography fontSize={13}>
                                      {`${
                                        Math.round(
                                          +summaryData("protein")?.calories
                                        ) || 0
                                      } Cals · ${
                                        Math.round(
                                          +summaryData("protein")?.protein
                                        ) || 0
                                      }g Protein · ${
                                        Math.round(
                                          +summaryData("protein")?.carbohydrate
                                        ) || 0
                                      }g Carbs · ${
                                        Math.round(
                                          +summaryData("protein")?.fiber
                                        ) || 0
                                      }g Fiber · ${
                                        Math.round(
                                          +summaryData("protein")?.fat
                                        ) || 0
                                      }g Fat`}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box
                                  gap="10px"
                                  borderRadius="16px"
                                  p="10px"
                                  mb={2}
                                  alignItems="center"
                                  display="flex"
                                  flexDirection="column"
                                  sx={{ background: styles.lightDiaryTracker }}
                                  justifyContent="center"
                                >
                                  <Box
                                    width={"100%"}
                                    display="flex"
                                    justifyContent="space-between"
                                  >
                                    <Box />
                                    <Typography
                                      mr={4}
                                      fontSize={"12px"}
                                      color="#8E8E8E"
                                    >
                                      (Protein grams)
                                    </Typography>
                                  </Box>
                                  {currentActiveData?.protein?.map((item) => (
                                    <Box
                                      width={"100%"}
                                      display="flex"
                                      justifyContent="space-between"
                                      key={item.key}
                                    >
                                      <Box
                                        display={"flex"}
                                        alignItems={"center"}
                                      >
                                        {showCheckboxes && (
                                          <CustomCheckbox
                                            checked={selectedItems.includes(
                                              item._id
                                            )}
                                            onChange={() =>
                                              handleCheckboxChange(item._id)
                                            }
                                          />
                                        )}
                                        <Box>
                                          <Box
                                            display={"flex"}
                                            alignItems="center"
                                            gap="10px"
                                          >
                                            <Tooltip
                                              title={
                                                optionsProteins?.find(
                                                  (option) =>
                                                    option.value === item.name
                                                )?.label ?? item.name
                                              }
                                            >
                                              <Typography
                                                fontSize={13}
                                                color={styles.color}
                                                textTransform={"capitalize"}
                                                sx={{
                                                  cursor: "pointer",
                                                  display: "block", // Ensures the container behaves as a block element
                                                  maxWidth: isMobile
                                                    ? "90px"
                                                    : "500px", // Set your fixed width (adjust as needed)
                                                  overflow: "hidden", // Hide overflow content
                                                  textOverflow: "ellipsis", // Show ellipsis when text overflows
                                                  whiteSpace: "nowrap" // Prevent text from wrapping
                                                }}
                                              >
                                                {optionsProteins?.find(
                                                  (option) =>
                                                    option.value === item.name
                                                )?.label ?? item.name}
                                              </Typography>
                                            </Tooltip>
                                            {item?.type === "customFood" ||
                                            item?.extraDetails?.isCustomFood ? (
                                              item?.extraDetails
                                                ?.isFromOneOff ? (
                                                <Fastfood
                                                  style={{
                                                    color: "wheat",
                                                    height: 16,
                                                    width: 16
                                                  }}
                                                />
                                              ) : (
                                                <Box
                                                  component="img"
                                                  src="/images/custom.png"
                                                  alt="base"
                                                  sx={{
                                                    height: 16,
                                                    width: 16,
                                                    objectFit: "cover"
                                                  }}
                                                />
                                              )
                                            ) : (
                                              <></>
                                            )}
                                            {item?.type === "meal" ? (
                                              <MealIcon
                                                style={{
                                                  width: 16,
                                                  height: 16
                                                }}
                                              />
                                            ) : item?.type === "recipe" ? (
                                              <RecipeIcon
                                                style={{
                                                  width: 16,
                                                  height: 16
                                                }}
                                              />
                                            ) : (
                                              <></>
                                            )}
                                          </Box>
                                          <Typography
                                            fontSize={10}
                                            color={styles.disabledColor}
                                          >
                                            {item.time}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box display="flex" alignItems={"center"}>
                                        <Typography
                                          fontSize={13}
                                          fontWeight={600}
                                        >
                                          {Math.round(
                                            updatedItemAccordingQuantity(item)
                                              ?.extraDetails?.serving?.protein
                                          )}
                                          g
                                        </Typography>
                                        <LongMenu
                                          onClickHandler={async (type) => {
                                            if (type === "edit") {
                                              setEditIngredientData(item);
                                              setIsAddingWeight("protein");
                                            } else {
                                              await deleteIngredients(
                                                currentLevel?.levelId,
                                                currentActiveData?._id,
                                                item?._id,
                                                activeDay
                                              );
                                              setIsRefreshNeeded(true);
                                              dispatch(
                                                getCurrentUserDiaries({
                                                  activeDay,
                                                  levelId: currentLevel?.levelId
                                                })
                                              );
                                            }
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  ))}
                                  <Button
                                    onClick={async () => {
                                      // await updateIngredients({
                                      //   diaryId: currentActiveData?._id,
                                      //   levelId: currentLevel?.levelId,
                                      //   protein: [],
                                      //   currentDayDate: activeDay
                                      // });
                                      // setIsRefreshNeeded(true);
                                      // dispatch(
                                      //   getCurrentUserDiaries({ activeDay,
                                      //     levelId: currentLevel?.levelId
                                      //   })
                                      // );
                                      setOpenConfirmModal("protein");
                                    }}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                      p: "2px 8px",
                                      color: styles.color,
                                      border: `1px solid ${styles.color}`,
                                      cursor: "pointer",
                                      textTransform: "none"
                                    }}
                                  >
                                    {/* <IconButton
                                      sx={{
                                        color: styles.textColorDiary,
                                        height: "17px",
                                        width: "17px",
                                        padding: 0,
                                        ":disabled": {
                                          color: styles.textColorDiary
                                        }
                                      }}
                                      disabled
                                    >
                                      <Add
                                        color={styles.textColorDiary}
                                        fontSize="12px"
                                      />
                                    </IconButton> */}
                                    <Typography
                                      color={styles.textColorDiary}
                                      fontSize="13px"
                                    >
                                      Clear all
                                    </Typography>
                                  </Button>
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                gap="10px"
                                borderRadius="16px"
                                p="20px"
                                alignItems="center"
                                display="flex"
                                flexDirection="column"
                                sx={{ background: styles.lightDiaryTracker }}
                                justifyContent="center"
                              >
                                {selectedLevel === "level1" ? (
                                  <></>
                                ) : (
                                  <Typography
                                    width={"100%"}
                                    display={"flex"}
                                    justifyContent={"flex-start"}
                                    fontWeight={600}
                                    color="#368AD0"
                                  >
                                    Protein{" "}
                                  </Typography>
                                )}
                                {currentActiveData?.protein?.map((item) => (
                                  <Box
                                    width={"100%"}
                                    display="flex"
                                    justifyContent="space-between"
                                    key={item.key}
                                  >
                                    <Box display={"flex"} alignItems={"center"}>
                                      {showCheckboxes && (
                                        <CustomCheckbox
                                          checked={selectedItems.includes(
                                            item._id
                                          )}
                                          onChange={() =>
                                            handleCheckboxChange(item._id)
                                          }
                                        />
                                      )}
                                      <Box>
                                        <Typography
                                          fontSize={13}
                                          color={styles.color}
                                          textTransform={"capitalize"}
                                        >
                                          {optionsProteins?.find(
                                            (option) =>
                                              option.value === item.name
                                          )?.label ?? item.name}
                                        </Typography>
                                        <Typography
                                          fontSize={10}
                                          color={styles.disabledColor}
                                        >
                                          {item.time}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box display="flex" alignItems={"center"}>
                                      <Typography
                                        fontSize={13}
                                        fontWeight={600}
                                      >
                                        {
                                          convertItemQuantityToGrams(item)
                                            ?.quantity
                                        }
                                        g
                                      </Typography>
                                      {!item?.extraProteinAdded ? (
                                        <LongMenu
                                          onClickHandler={async (type) => {
                                            if (type === "edit") {
                                              setEditIngredientData(item);
                                              setIsAddingWeight("protein");
                                            } else {
                                              await deleteIngredients(
                                                currentLevel?.levelId,
                                                currentActiveData?._id,
                                                item?._id,
                                                activeDay
                                              );
                                              setIsRefreshNeeded(true);
                                              dispatch(
                                                getCurrentUserDiaries({
                                                  activeDay,
                                                  levelId: currentLevel?.levelId
                                                })
                                              );
                                            }
                                          }}
                                        />
                                      ) : (
                                        <Box height={28} width={32}></Box>
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                                <Button
                                  onClick={async () => {
                                    // await updateIngredients({
                                    //   diaryId: currentActiveData?._id,
                                    //   levelId: currentLevel?.levelId,
                                    //   protein: [],
                                    //   currentDayDate: activeDay
                                    // });
                                    // setIsRefreshNeeded(true);
                                    // dispatch(
                                    //   getCurrentUserDiaries({ activeDay,
                                    //     levelId: currentLevel?.levelId
                                    //   })
                                    // );
                                    setOpenConfirmModal("protein");
                                  }}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    p: "2px 8px",
                                    color: styles.color,
                                    border: `1px solid ${styles.color}`,
                                    cursor: "pointer",
                                    textTransform: "none"
                                  }}
                                >
                                  {/* <IconButton
                                    sx={{
                                      color: styles.textColorDiary,
                                      height: "17px",
                                      width: "17px",
                                      padding: 0,
                                      ":disabled": {
                                        color: styles.textColorDiary
                                      }
                                    }}
                                    disabled
                                  >
                                    <Add
                                      color={styles.textColorDiary}
                                      fontSize="12px"
                                    />
                                  </IconButton> */}
                                  <Typography
                                    color={styles.textColorDiary}
                                    fontSize="13px"
                                  >
                                    Clear all
                                  </Typography>
                                </Button>
                              </Box>
                            )
                          ) : (
                            <Box
                              gap="10px"
                              borderRadius="16px"
                              p="20px"
                              mt={-1}
                              alignItems="center"
                              display="flex"
                              flexDirection="column"
                              sx={{ background: styles.lightDiaryTracker }}
                              justifyContent="center"
                            >
                              <Box
                                display="flex"
                                width={"100%"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                              >
                                <Typography
                                  width={"100%"}
                                  display={"flex"}
                                  justifyContent={"flex-start"}
                                  fontWeight={600}
                                  color="#368AD0"
                                >
                                  Protein{" "}
                                </Typography>
                                {selectedLevel === "level3" && (
                                  <IconButton
                                    onClick={() => {
                                      handleClose();
                                      setEditIngredientData();
                                      setIsAddingWeight("protein");
                                    }}
                                  >
                                    <Add sx={{ color: "#368AD0" }} />
                                  </IconButton>
                                )}
                              </Box>
                              <NoEntry />
                              <Typography color="#6B7280">No entry</Typography>
                            </Box>
                          )}
                        </>
                      )}
                      {currentLevel?.levelName === "level3" && (
                        <>
                          {currentActiveData?.otherFood?.length ? (
                            selectedLevel === "level3" ? (
                              <Box
                                display={"flex"}
                                flexDirection={"column"}
                                gap={2}
                              >
                                <Box
                                  display={"flex"}
                                  gap={0.4}
                                  // flexDirection="column"
                                  borderRadius="8px"
                                  padding={"12px"}
                                  width={"100%"}
                                  sx={{ background: styles.lightGreyColor }}
                                >
                                  <Box
                                    flexDirection="column"
                                    display="flex"
                                    justifyContent="space-between"
                                    width="100%"
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      width="100%"
                                    >
                                      <Typography
                                        fontWeight="600"
                                        color="#FF9431"
                                      >
                                        {"Other"}
                                      </Typography>{" "}
                                      <IconButton
                                        onClick={() => {
                                          handleClose();
                                          setEditIngredientData();
                                          setIsAddingWeight("otherFood");
                                        }}
                                      >
                                        <Add sx={{ color: "#FF9431" }} />
                                      </IconButton>
                                    </Box>
                                    <Typography fontSize={13}>
                                      {`${
                                        Math.round(
                                          +summaryData("otherFood")?.calories
                                        ) || 0
                                      } Cals · ${
                                        Math.round(
                                          +summaryData("otherFood")?.protein
                                        ) || 0
                                      }g Protein · ${
                                        Math.round(
                                          +summaryData("otherFood")
                                            ?.carbohydrate
                                        ) || 0
                                      }g Carbs · ${
                                        Math.round(
                                          +summaryData("otherFood")?.fiber
                                        ) || 0
                                      }g Fiber · ${
                                        Math.round(
                                          +summaryData("otherFood")?.fat
                                        ) || 0
                                      }g Fat`}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box
                                  gap="10px"
                                  borderRadius="16px"
                                  p="10px"
                                  mb={2}
                                  alignItems="center"
                                  display="flex"
                                  flexDirection="column"
                                  sx={{ background: styles.lightDiaryTracker }}
                                  justifyContent="center"
                                >
                                  <Box
                                    width={"100%"}
                                    display="flex"
                                    justifyContent="space-between"
                                  >
                                    <Box />
                                    <Typography
                                      mr={4}
                                      fontSize={"12px"}
                                      color="#8E8E8E"
                                    >
                                      (Calories)
                                    </Typography>
                                  </Box>
                                  {currentActiveData?.otherFood?.map((item) => (
                                    <Box
                                      width={"100%"}
                                      display="flex"
                                      justifyContent="space-between"
                                      key={item.key}
                                    >
                                      <Box
                                        display={"flex"}
                                        alignItems={"center"}
                                      >
                                        {showCheckboxes && (
                                          <CustomCheckbox
                                            checked={selectedItems.includes(
                                              item._id
                                            )}
                                            onChange={() =>
                                              handleCheckboxChange(item._id)
                                            }
                                          />
                                        )}
                                        <Box>
                                          <Box
                                            display={"flex"}
                                            alignItems="center"
                                            gap="10px"
                                          >
                                            <Tooltip title={item.name}>
                                              <Typography
                                                fontSize={13}
                                                color={styles.color}
                                                textTransform={"capitalize"}
                                                sx={{
                                                  cursor: "pointer",
                                                  display: "block", // Ensures the container behaves as a block element
                                                  maxWidth: isMobile
                                                    ? "90px"
                                                    : "500px", // Set your fixed width (adjust as needed)
                                                  overflow: "hidden", // Hide overflow content
                                                  textOverflow: "ellipsis", // Show ellipsis when text overflows
                                                  whiteSpace: "nowrap" // Prevent text from wrapping
                                                }}
                                              >
                                                {item.name}
                                              </Typography>
                                            </Tooltip>
                                            {item?.type === "customFood" ||
                                            item?.extraDetails?.isCustomFood ? (
                                              item?.extraDetails
                                                ?.isFromOneOff ? (
                                                <Fastfood
                                                  style={{
                                                    color: "wheat",
                                                    height: 16,
                                                    width: 16
                                                  }}
                                                />
                                              ) : (
                                                <Box
                                                  component="img"
                                                  src="/images/custom.png"
                                                  alt="base"
                                                  sx={{
                                                    height: 16,
                                                    width: 16,
                                                    objectFit: "cover"
                                                  }}
                                                />
                                              )
                                            ) : (
                                              <></>
                                            )}
                                            {item?.type === "meal" ? (
                                              <MealIcon
                                                style={{
                                                  width: 16,
                                                  height: 16
                                                }}
                                              />
                                            ) : item?.type === "recipe" ? (
                                              <RecipeIcon
                                                style={{
                                                  width: 16,
                                                  height: 16
                                                }}
                                              />
                                            ) : (
                                              <></>
                                            )}
                                          </Box>
                                          <Typography
                                            fontSize={10}
                                            color={styles.disabledColor}
                                          >
                                            {item.time}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box display="flex" alignItems={"center"}>
                                        <Typography
                                          fontSize={13}
                                          fontWeight={600}
                                        >
                                          {Math.round(
                                            updatedItemAccordingQuantity(item)
                                              ?.extraDetails?.serving?.calories
                                          )}
                                        </Typography>
                                        <LongMenu
                                          onClickHandler={async (type) => {
                                            if (type === "edit") {
                                              setEditIngredientData(item);
                                              setIsAddingWeight("otherFood");
                                            } else {
                                              await deleteIngredients(
                                                currentLevel?.levelId,
                                                currentActiveData?._id,
                                                item?._id,
                                                activeDay
                                              );
                                              setIsRefreshNeeded(true);
                                              dispatch(
                                                getCurrentUserDiaries({
                                                  activeDay,
                                                  levelId: currentLevel?.levelId
                                                })
                                              );
                                            }
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  ))}
                                  <Button
                                    onClick={async () => {
                                      // await updateIngredients({
                                      //   diaryId: currentActiveData?._id,
                                      //   levelId: currentLevel?.levelId,
                                      //   otherFood: [],
                                      //   currentDayDate: activeDay
                                      // });
                                      // setIsRefreshNeeded(true);
                                      // dispatch(
                                      //   getCurrentUserDiaries({ activeDay,
                                      //     levelId: currentLevel?.levelId
                                      //   })
                                      // );
                                      setOpenConfirmModal("otherFood");
                                    }}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                      p: "2px 8px",
                                      color: styles.color,
                                      border: `1px solid ${styles.color}`,
                                      cursor: "pointer",
                                      textTransform: "none"
                                    }}
                                  >
                                    {/* <IconButton
                                      sx={{
                                        color: styles.textColorDiary,
                                        height: "17px",
                                        width: "17px",
                                        padding: 0,
                                        ":disabled": {
                                          color: styles.textColorDiary
                                        }
                                      }}
                                      disabled
                                    >
                                      <Add
                                        color={styles.textColorDiary}
                                        fontSize="12px"
                                      />
                                    </IconButton> */}
                                    <Typography
                                      color={styles.textColorDiary}
                                      fontSize="13px"
                                    >
                                      Clear all
                                    </Typography>
                                  </Button>
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                gap="10px"
                                borderRadius="16px"
                                p="20px"
                                alignItems="center"
                                display="flex"
                                flexDirection="column"
                                sx={{ background: styles.lightDiaryTracker }}
                                justifyContent="center"
                              >
                                {currentActiveData?.otherFood?.map((item) => (
                                  <Box
                                    width={"100%"}
                                    display="flex"
                                    justifyContent="space-between"
                                    key={item.key}
                                  >
                                    <Box display={"flex"} alignItems={"center"}>
                                      {showCheckboxes && (
                                        <CustomCheckbox
                                          checked={selectedItems.includes(
                                            item._id
                                          )}
                                          onChange={() =>
                                            handleCheckboxChange(item._id)
                                          }
                                        />
                                      )}
                                      <Box>
                                        <Typography
                                          fontSize={13}
                                          color={styles.color}
                                          textTransform={"capitalize"}
                                        >
                                          {optionsProteins?.find(
                                            (option) =>
                                              option.value === item.name
                                          )?.label ?? item.name}
                                        </Typography>
                                        <Typography
                                          fontSize={10}
                                          color={styles.disabledColor}
                                        >
                                          {item.time}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box display="flex" alignItems={"center"}>
                                      <Typography
                                        fontSize={13}
                                        fontWeight={600}
                                      >
                                        {
                                          convertItemQuantityToGrams(item)
                                            ?.quantity
                                        }
                                        g
                                      </Typography>
                                      <LongMenu
                                        onClickHandler={async (type) => {
                                          if (type === "edit") {
                                            setEditIngredientData(item);
                                            setIsAddingWeight("otherFood");
                                          } else {
                                            await deleteIngredients(
                                              currentLevel?.levelId,
                                              currentActiveData?._id,
                                              item?._id,
                                              activeDay
                                            );
                                            setIsRefreshNeeded(true);
                                            dispatch(
                                              getCurrentUserDiaries({
                                                activeDay,
                                                levelId: currentLevel?.levelId
                                              })
                                            );
                                          }
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                ))}
                                <Button
                                  onClick={async () => {
                                    // await updateIngredients({
                                    //   diaryId: currentActiveData?._id,
                                    //   levelId: currentLevel?.levelId,
                                    //   otherFood: [],
                                    //   currentDayDate: activeDay
                                    // });
                                    // setIsRefreshNeeded(true);
                                    // dispatch(
                                    //   getCurrentUserDiaries({ activeDay,
                                    //     levelId: currentLevel?.levelId
                                    //   })
                                    // );
                                    setOpenConfirmModal("otherFood");
                                  }}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    p: "2px 8px",
                                    color: styles.color,
                                    border: `1px solid ${styles.color}`,
                                    cursor: "pointer",
                                    textTransform: "none"
                                  }}
                                >
                                  {/* <IconButton
                                    sx={{
                                      color: styles.textColorDiary,
                                      height: "17px",
                                      width: "17px",
                                      padding: 0,
                                      ":disabled": {
                                        color: styles.textColorDiary
                                      }
                                    }}
                                    disabled
                                  >
                                    <Add
                                      color={styles.textColorDiary}
                                      fontSize="12px"
                                    />
                                  </IconButton> */}
                                  <Typography
                                    color={styles.textColorDiary}
                                    fontSize="13px"
                                  >
                                    Clear all
                                  </Typography>
                                </Button>
                              </Box>
                            )
                          ) : (
                            <Box
                              gap="10px"
                              borderRadius="16px"
                              p="20px"
                              mt={-1}
                              alignItems="center"
                              display="flex"
                              flexDirection="column"
                              sx={{ background: styles.lightDiaryTracker }}
                              justifyContent="center"
                            >
                              <Box
                                display="flex"
                                width={"100%"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                              >
                                <Typography
                                  width={"100%"}
                                  display={"flex"}
                                  justifyContent={"flex-start"}
                                  fontWeight={600}
                                  color={"#ff9431"}
                                >
                                  Other{" "}
                                </Typography>
                                {selectedLevel === "level3" && (
                                  <IconButton
                                    onClick={() => {
                                      handleClose();
                                      setEditIngredientData();
                                      setIsAddingWeight("otherFood");
                                    }}
                                  >
                                    <Add sx={{ color: "#FF9431" }} />
                                  </IconButton>
                                )}
                              </Box>
                              <NoEntry />
                              <Typography color="#6B7280">No entry</Typography>
                            </Box>
                          )}
                        </>
                      )}
                      {!currentActiveData?.notes && !isAddingNote && (
                        <Button
                          onClick={() => setIsAddingNote(true)}
                          sx={{
                            width: "120px",
                            textTransform: "none",
                            color: "#fcfcfc",
                            height: "40px",
                            fontWeight: 600,
                            padding: "12px",
                            gap: "8px",
                            borderRadius: "4px"
                          }}
                        >
                          <Add
                            sx={{ height: 16, width: 16, color: styles.yellow }}
                          />
                          <Typography
                            sx={{ fontSize: "14px", fontWeight: 600 }}
                            color={styles.yellow}
                          >
                            Add Note
                          </Typography>
                        </Button>
                      )}

                      {(isAddingNote || currentActiveData?.notes) && (
                        <Box
                          width="100%"
                          display="flex"
                          flexDirection="column"
                          gap="10px"
                          sx={{
                            background: styles.lightDiaryTracker,
                            borderRadius: "8px",
                            p: "12px 20px"
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              fontWeight={600}
                              fontSize="16px"
                              color={styles.yellow}
                            >
                              Notes
                            </Typography>
                            {!isAddingNote && currentActiveData?.notes && (
                              <IconButton
                                onClick={() => setIsAddingNote(true)}
                                size="small"
                              >
                                <Edit
                                  sx={{ color: styles.yellow, fontSize: 18 }}
                                />
                              </IconButton>
                            )}
                          </Box>

                          {isAddingNote ? (
                            <>
                              <Box position="relative" mt={0}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={3}
                                  maxRows={10}
                                  variant="outlined"
                                  placeholder="Write your note..."
                                  value={noteText}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    if (newValue.length <= 350) {
                                      setNoteText(newValue);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    // This is optional — only needed if you want to do something on Enter
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault(); // prevent parent form submit if needed
                                      // Optionally, handle enter-specific logic here
                                    }
                                  }}
                                  InputProps={{
                                    sx: {
                                      color: styles.color,
                                      "& fieldset": {
                                        borderColor: styles.notesColor
                                      },
                                      "&:hover fieldset": {
                                        borderColor: styles.notesColor
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: styles.notesColor
                                      },
                                      borderRadius: "4px"
                                    }
                                  }}
                                  sx={{
                                    backgroundColor: "transparent",
                                    "& .MuiOutlinedInput-root": {
                                      "& fieldset": {
                                        borderColor: styles.notesColor
                                      },
                                      "&:hover fieldset": {
                                        borderColor: styles.notesColor
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: styles.notesColor
                                      }
                                    },
                                    "& .MuiInputBase-input": {
                                      color: styles.color
                                    }
                                  }}
                                />
                              </Box>

                              <Box
                                display="flex"
                                justifyContent="flex-end"
                                gap="10px"
                              >
                                <IconButton
                                  onClick={async () => {
                                    await updateIngredients({
                                      diaryId: currentActiveData?._id,
                                      levelId: currentActiveData?.levelId,
                                      currentDayDate:
                                        currentActiveData?.currentDayDate,
                                      notes: noteText?.trim()
                                    });
                                    setIsAddingNote(false);
                                    setIsRefreshNeeded(true);
                                    dispatch(
                                      getCurrentUserDiaries({
                                        activeDay,
                                        levelId: currentLevel?.levelId
                                      })
                                    );
                                    // Optionally: call your saveNote function here
                                  }}
                                >
                                  <Check sx={{ color: "#28a745" }} />
                                </IconButton>
                                <IconButton
                                  onClick={() => {
                                    setIsAddingNote(false);
                                    // setNoteText(currentActiveData?.notes || "");
                                  }}
                                >
                                  <Close sx={{ color: "#dc3545" }} />
                                </IconButton>
                              </Box>
                            </>
                          ) : (
                            <Typography
                              fontSize="14px"
                              color={styles.inputLabelColor}
                            >
                              {currentActiveData.notes}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )
              ) : (
                <></>
              )}
            </Box>
          )}
        </Box>
      )}
      {activeLevelForMobile && isMobile && (
        <Box
          display="flex"
          sx={{
            width: "100%",
            padding: isViewingUserMeals ? 0 : "10px 12px"
          }}
          flexDirection="column"
          gap={3}
        >
          {isViewingUserMeals ? (
            <></>
          ) : isShowingAllStats ? (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={"16%"}
              mr={isMobile ? 2 : 0}
            >
              <IconButton
                onClick={() => {
                  setIsShowingAllStats(false);
                }}
              >
                <ChevronLeft
                  sx={{
                    width: 30,
                    height: 30,
                    color: styles.color
                  }}
                  width={30}
                  height={30}
                  color={styles.color}
                />
              </IconButton>

              <Typography mr={1.5} fontWeight={600} fontSize={"28px"}>
                Stats
              </Typography>
              <div />
            </Box>
          ) : isShowingLeaderBoard ? (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={"16%"}
              mr={isMobile ? 2 : 0}
            >
              <IconButton
                onClick={() => {
                  setIsShowingLeaderBoard(false);
                  if (leaderboard && !fromPoints) {
                    navigate("/profile");
                  }
                }}
              >
                <ChevronLeft
                  sx={{
                    width: 30,
                    height: 30,
                    color: styles.color
                  }}
                />
              </IconButton>
              <Typography mr={1.5} fontWeight={600} fontSize={"28px"}>
                Leaderboard
              </Typography>
              <div />
            </Box>
          ) : isShowingCreateMeal || editMealToDiary ? (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={"16%"}
              mr={isMobile ? 2 : 0}
            >
              <IconButton
                onClick={() => {
                  if (selectedOptionData?.serving) {
                    setSelectedOptionData(null);
                  } else if (isAddingMealItem) {
                    setIsAddingMealItem(false);
                  } else if (editMealToDiary) {
                    setEditMealToDiary(false);
                  } else {
                    setIsShowingCreateMeal(false);
                  }
                }}
              >
                <ChevronLeft
                  sx={{
                    width: 30,
                    height: 30,
                    color: styles.color
                  }}
                />
              </IconButton>
              <Typography fontWeight={600} fontSize={"18px"}>
                {!isShowingCreateMeal
                  ? "My Meals"
                  : `Add Meal ${isAddingMealItem ? "Item" : ""}`}
              </Typography>
              <div />
            </Box>
          ) : isShowingCreateRecipe || editRecipeToDiary ? (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={"16%"}
              mr={isMobile ? 2 : 0}
            >
              <IconButton
                onClick={() => {
                  if (selectedOptionData?.serving) {
                    setSelectedOptionData(null);
                  } else if (isAddingMealItem) {
                    setIsAddingMealItem(false);
                  } else if (editRecipeToDiary) {
                    setEditRecipeToDiary(false);
                  } else {
                    setIsShowingCreateRecipe(false);
                  }
                }}
              >
                <ChevronLeft
                  sx={{
                    width: 30,
                    height: 30,
                    color: styles.color
                  }}
                />
              </IconButton>
              <Typography fontWeight={600} fontSize={"18px"}>
                {!isShowingCreateRecipe
                  ? "My Recipes"
                  : `Add Recipe ${isAddingMealItem ? "Item" : ""}`}
              </Typography>
              <div />
            </Box>
          ) : isShowingCreateCustomFood && editOneOffToDiary ? (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={"16%"}
              mr={isMobile ? 2 : 0}
            >
              <IconButton
                onClick={() => {
                  if (selectedOptionData?.serving) {
                    setSelectedOptionData(null);
                  } else if (editOneOffToDiary) {
                    setEditOneOffToDiary(false);
                  }
                  setIsShowingCreateCustomFood(false);
                }}
              >
                <ChevronLeft
                  sx={{
                    width: 30,
                    height: 30,
                    color: styles.color
                  }}
                />
              </IconButton>
              <Typography fontWeight={600} fontSize={"18px"}>
                One Off
              </Typography>
              <div />
            </Box>
          ) : isShowingCreateCustomFood || editCustomFoodToDiary ? (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={"16%"}
              mr={isMobile ? 2 : 0}
            >
              <IconButton
                onClick={() => {
                  if (selectedOptionData?.serving) {
                    setSelectedOptionData(null);
                  } else if (editCustomFoodToDiary) {
                    setEditCustomFoodToDiary(false);
                  } else {
                    setIsShowingCreateCustomFood(false);
                  }
                }}
              >
                <ChevronLeft
                  sx={{
                    width: 30,
                    height: 30,
                    color: styles.color
                  }}
                />
              </IconButton>
              <Typography fontWeight={600} fontSize={"18px"}>
                My Foods
              </Typography>
              <div />
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={"16%"}
            >
              {/* <IconButton
                onClick={() => {
                  setActiveLevelForMobile(false);
                }}
              >
                <ChevronLeft
                  sx={{
                    width: 30,
                    height: 30,
                    color: styles.color
                  }}
                  width={30}
                  height={30}
                  color={styles.color}
                />
              </IconButton> */}
              <MobileLevelsDropdown
                allLevels={allLevels}
                selectedLevel={selectedLevel}
                handleLevelSelect={handleLevelSelect}
                setActiveLevelForMobile={setActiveLevelForMobile}
                userData={userData}
                setSnackbarMessage={setSnackbarMessage}
                setOpenSnackbar={setOpenSnackbar}
              />
              <PickerWithButtonField
                maxDate={dayjs(userData?.["createdAt"])
                  .startOf("month")
                  .format("YYYY-MM-DD")}
                allCompletedDates={allCompletedData}
                date={date}
                userData={userData}
                levelData={levelData}
                setOnTodayClicked={setOnTodayClicked}
                visibleDaysCount={visibleDaysCount}
                setStartDay={setStartDay}
                setDate={setDate}
                setActiveDay={setActiveDay}
                activeDay={activeDay}
                startDay={startDay}
                visibleDays={visibleDays}
                days={days}
                diaryData={diaryData}
                handleDayClick={handleDayClick}
                handleNext={handleNext}
                handlePrevious={handlePrevious}
              />
              <ThreeDotMenu
                currentLevel={currentLevel}
                isLevel3={currentLevel?.levelName === "level3"}
                onClickHandler={async (type) => {
                  if (type === "stats") setIsShowingAllStats(true);
                  else if (type === "multiSelect") {
                    setShowCheckboxes(true);
                  } else if (type === "pasteMultiSelectData") {
                    try {
                      await pasteMultiSelectData({
                        currentDayDate: activeDay,
                        levelId: currentLevel?.levelId,
                        selectedIngredientIds: selectedItems,
                        outOf800g: currentActiveData?.outOf800g,
                        outOfProtein: currentActiveData?.outOfProtein,
                        outOfCalories: currentActiveData?.outOfCalories,
                        currentDiaryId: currentActiveData?._id
                      });
                      setShowCheckboxes(false);
                      localStorage.removeItem("copyIngredientsData");
                      setSelectedItems([]);
                      setIsRefreshNeeded(true);
                      dispatch(
                        getCurrentUserDiaries({
                          activeDay,
                          levelId: currentLevel?.levelId
                        })
                      );
                      localStorage.setItem(
                        "lastDiaryVisitedLevel",
                        JSON.stringify({
                          activeDay: activeDay,
                          lastUpdated: activeDay,
                          level: currentLevel?.levelName,
                          unit: "grams"
                        })
                      );
                    } catch (err) {
                      setSnackbarMessage(err?.error);
                      setOpenSnackbar(true);
                    }
                  } else if (type === "copy") {
                    const isConditionMet = () => {
                      const previousDayData = diaryData?.find(
                        (item) =>
                          item.currentDayDate ===
                          dayjs(activeDay)
                            ?.subtract(1, "day")
                            ?.format("YYYY-MM-DD")
                      );

                      const is800gEmpty =
                        previousDayData?.entry800g?.length === 0;
                      const isProteinEmpty =
                        selectedLevel !== "level1"
                          ? previousDayData?.protein?.length === 0
                          : true;
                      const isOtherFoodEmpty =
                        selectedLevel === "level3"
                          ? previousDayData?.otherFood?.length === 0
                          : true;

                      return is800gEmpty && isProteinEmpty && isOtherFoodEmpty;
                    };

                    if (isConditionMet()) {
                      setSnackbarMessage("No data for yesterday");
                      setOpenSnackbar(true);
                      return;
                    }

                    const previousDay = dayjs(activeDay)
                      ?.subtract(1, "day")
                      ?.format("YYYY-MM-DD");
                    const previousDayData = diaryData?.find(
                      (item) => item.currentDayDate === previousDay
                    );
                    const tomorrow = dayjs().add(1, "day").startOf("day");

                    // Check if the input date is tomorrow
                    const isTomorrow = dayjs(activeDay).isSame(tomorrow, "day");

                    await copyYesterdayData({
                      levelId: currentLevel?.levelId,
                      previousDiaryId: previousDayData?._id,
                      currentDiaryId: currentActiveData?._id,
                      currentDayDate: activeDay,
                      completionNotNeeded: isTomorrow
                    });

                    localStorage.setItem(
                      "lastDiaryVisitedLevel",
                      JSON.stringify({
                        level: currentLevel?.levelName,
                        activeDay,
                        lastUpdated: dayjs().format("YYYY-MM-DD")
                      })
                    );

                    setIsRefreshNeeded(true);
                    setTimeout(() => {
                      dispatch(
                        getCurrentUserDiaries({
                          activeDay,
                          levelId: currentLevel?.levelId
                        })
                      );
                    }, 2000);
                  } else if (type === "leaderboard") {
                    setIsShowingLeaderBoard(true);
                    navigate("/diary", { replace: true });
                  } else if (type === "createMeal") {
                    setEditMealToDiary(true);
                    // dispatch(updateAddingMeal({ meal: {} }));
                  } else if (type === "createRecipe") {
                    setEditRecipeToDiary(true);
                    // dispatch(updateAddingRecipe({ recipe: {} }));
                  } else if (type === "addCustomFoods") {
                    setEditCustomFoodToDiary(true);
                    // dispatch(updateAddingRecipe({ recipe: {} }));
                    setIsShowingCreateCustomFood(false);
                  } else if (type === "addOneOff") {
                    setEditOneOffToDiary(true);
                    setIsShowingCreateCustomFood(true);
                  }
                }}
              />
            </Box>
          )}
          {isShowingAllStats ? (
            <StatsList level={currentLevel} completedDates={allCompletedData} />
          ) : isShowingLeaderBoard ? (
            <Leaderboard
              isViewingUserMeals={isViewingUserMeals}
              setIsViewingUserMeals={setIsViewingUserMeals}
              levelId={currentLevel?.levelId}
              selectedChallengeId={leaderboard}
            />
          ) : editMealToDiary ? (
            <AddMealOrRecipeToDiary
              currentLevel={currentLevel}
              levelId={currentLevel?.levelId}
              setIsRefreshNeeded={setIsRefreshNeeded}
              activeDay={activeDay}
              setIsShowingCreateMeal={setIsShowingCreateMeal}
              setIsShowingCreateRecipe={setIsShowingCreateRecipe}
              setEditMealToDiary={setEditMealToDiary}
              setEditRecipeToDiary={setEditRecipeToDiary}
              type="meal"
            />
          ) : editRecipeToDiary ? (
            <AddMealOrRecipeToDiary
              currentLevel={currentLevel}
              levelId={currentLevel?.levelId}
              setIsRefreshNeeded={setIsRefreshNeeded}
              activeDay={activeDay}
              setIsShowingCreateMeal={setIsShowingCreateMeal}
              setIsShowingCreateRecipe={setIsShowingCreateRecipe}
              setEditMealToDiary={setEditMealToDiary}
              setEditRecipeToDiary={setEditRecipeToDiary}
              type="recipe"
            />
          ) : editCustomFoodToDiary ? (
            <AddMealOrRecipeToDiary
              currentLevel={currentLevel}
              levelId={currentLevel?.levelId}
              setIsRefreshNeeded={setIsRefreshNeeded}
              activeDay={activeDay}
              setIsShowingCreateMeal={setIsShowingCreateMeal}
              setIsShowingCreateRecipe={setIsShowingCreateRecipe}
              setEditMealToDiary={setEditMealToDiary}
              setEditRecipeToDiary={setEditRecipeToDiary}
              customFoodFormData={customFoodFormData}
              setCustomFoodFormData={setCustomFoodFormData}
              setIsShowingCreateCustomFood={setIsShowingCreateCustomFood}
              setEditCustomFoodToDiary={setEditCustomFoodToDiary}
              type="custom food"
            />
          ) : isShowingCreateMeal ? (
            <AddMealForLevel3
              setEditRecipeToDiary={setEditRecipeToDiary}
              activeDay={activeDay}
              currentLevel={currentLevel}
              setIsRefreshNeeded={setIsRefreshNeeded}
              setEditMealToDiary={setEditMealToDiary}
              setIsAddingWeight={setIsAddingWeight}
              setEditCustomFoodToDiary={setEditCustomFoodToDiary}
              setIsShowingCreateMeal={setIsShowingCreateMeal}
              isAddingMealItem={isAddingMealItem}
              setIsShowingCreateRecipe={setIsShowingCreateRecipe}
              setIsAddingMealItem={setIsAddingMealItem}
              selectedOptionData={selectedOptionData}
              setSelectedOptionData={setSelectedOptionData}
              type="meal"
              levelId={currentLevel?.levelId}
            />
          ) : isShowingCreateRecipe ? (
            <AddMealForLevel3
              setEditRecipeToDiary={setEditRecipeToDiary}
              activeDay={activeDay}
              currentLevel={currentLevel}
              setIsRefreshNeeded={setIsRefreshNeeded}
              setEditMealToDiary={setEditMealToDiary}
              setIsAddingWeight={setIsAddingWeight}
              setEditCustomFoodToDiary={setEditCustomFoodToDiary}
              setIsShowingCreateMeal={setIsShowingCreateMeal}
              setIsShowingCreateRecipe={setIsShowingCreateRecipe}
              isAddingMealItem={isAddingMealItem}
              setIsAddingMealItem={setIsAddingMealItem}
              selectedOptionData={selectedOptionData}
              setSelectedOptionData={setSelectedOptionData}
              type="recipe"
              levelId={currentLevel?.levelId}
            />
          ) : isShowingCreateCustomFood ? (
            <Box>
              <AddMealForLevel3
                setEditRecipeToDiary={setEditRecipeToDiary}
                activeDay={activeDay}
                currentLevel={currentLevel}
                setIsRefreshNeeded={setIsRefreshNeeded}
                setEditMealToDiary={setEditMealToDiary}
                setIsAddingWeight={setIsAddingWeight}
                setEditCustomFoodToDiary={setEditCustomFoodToDiary}
                setIsShowingCreateCustomFood={setIsShowingCreateCustomFood}
                setIsShowingCreateRecipe={setIsShowingCreateRecipe}
                setIsShowingCreateMeal={setIsShowingCreateMeal}
                isAddingMealItem={isAddingMealItem}
                setIsAddingMealItem={setIsAddingMealItem}
                selectedOptionData={selectedOptionData}
                setSelectedOptionData={setSelectedOptionData}
                customFoodFormData={customFoodFormData}
                setCustomFoodFormData={setCustomFoodFormData}
                type={"customFood"}
                editOneOffToDiary={editOneOffToDiary}
                setEditOneOffToDiary={setEditOneOffToDiary}
                levelId={currentLevel?.levelId}
              />
            </Box>
          ) : (
            <>
              {/* <WeekView
                allCompletedData={allCompletedData}
                activeDay={activeDay}
                setActiveDay={setActiveDay}
                startDay={startDay}
                visibleDays={visibleDays}
                visibleDaysCount={visibleDaysCount}
                days={days}
                setStartDay={setStartDay}
                diaryData={diaryData}
                handleDayClick={handleDayClick}
                handleNext={handleNext}
                handlePrevious={handlePrevious}
              />{" "} */}
              <Box
                display="flex"
                flexDirection="column"
                gap={"20px"}
                sx={{
                  ...((!isMobile || !isTablet) && {
                    overflowY: "auto",
                    // maxHeight: "calc(100vh - 120px)",
                    scrollbarWidth: "none",
                    ":webkit-scrollbar": {
                      display: "none"
                    }
                  })
                }}
              >
                {/* <Box display="flex" justifyContent="space-between">
                  <Typography
                    color={styles.color}
                    fontWeight={600}
                    fontSize={"24px"}
                  >
                    {formatDate(activeDay, false)} (
                    {dayjs(activeDay).format(isMobile ? "ddd" : "dddd")})
                  </Typography>
                  <CopyYesterdayButton
                    diaryData={diaryData}
                    activeDay={activeDay}
                    selectedLevel={selectedLevel}
                    currentLevel={currentLevel}
                    currentActiveData={currentActiveData}
                    setSnackbarMessage={setSnackbarMessage}
                    setOpenSnackbar={setOpenSnackbar}
                    setIsRefreshNeeded={setIsRefreshNeeded}
                    dispatch={dispatch}
                    copyYesterdayData={copyYesterdayData}
                  />
                </Box> */}
                <Box
                  display="flex"
                  borderRadius={"16px"}
                  sx={{ background: styles.diaryTracker }}
                  justifyContent="center"
                >
                  {diaryLoading || !currentLevel?.levelName ? (
                    <CircularProgress sx={{ my: 12 }} size={40} />
                  ) : (
                    <>
                      <DiaryTracker
                        loading={diaryLoading}
                        levelName={currentLevel?.levelName}
                        openWeightModal={openWeightModal}
                        setOpenWeightModal={setOpenWeightModal}
                        title={"#800gChallenge"}
                        levelId={currentLevel?.levelId}
                        diaryId={currentActiveData?._id || ""}
                        value={
                          selectedLevel === "level3"
                            ? Math.round(
                                (summaryData("entry800g")
                                  ?.metric_serving_amount || 0) +
                                  sumCustomFoodServingAmounts(currentActiveData)
                              )
                            : getTotalWeight(currentActiveData?.entry800g) || 0
                        }
                        strokeColor={"#F6541C"}
                        maxValue={currentActiveData?.outOf800g}
                        setIsRefreshNeeded={setIsRefreshNeeded}
                        dayName={activeDay}
                        heightX={
                          currentLevel?.levelName === "level2" ? 130 : 170
                        }
                        widthX={
                          currentLevel?.levelName === "level2" ? 190 : 230
                        }
                      />
                      {currentLevel?.levelName !== "level1" && (
                        <DiaryTracker
                          loading={diaryLoading}
                          levelName={currentLevel?.levelName}
                          openWeightModal={openWeightModal}
                          setOpenWeightModal={setOpenWeightModal}
                          title={"Protein"}
                          levelId={currentLevel?.levelId}
                          diaryId={currentActiveData?._id || ""}
                          value={
                            selectedLevel === "level3"
                              ? Math.round(
                                  summaryData("entry800g")?.protein +
                                    summaryData("protein")?.protein +
                                    summaryData("otherFood")?.protein
                                )
                              : getTotalWeight(currentActiveData?.protein) || 0
                          }
                          strokeColor={"#368AD0"}
                          maxValue={currentActiveData?.outOfProtein}
                          setIsRefreshNeeded={setIsRefreshNeeded}
                          dayName={activeDay}
                          heightX={
                            currentLevel?.levelName === "level2" ? 130 : 250
                          }
                          widthX={
                            currentLevel?.levelName === "level2" ? 190 : 350
                          }
                        />
                      )}
                      {currentLevel?.levelName === "level3" && (
                        <DiaryTracker
                          loading={diaryLoading}
                          isCalories
                          levelName={currentLevel?.levelName}
                          openWeightModal={openWeightModal}
                          setOpenWeightModal={setOpenWeightModal}
                          title={"Calories"}
                          levelId={currentLevel?.levelId}
                          diaryId={currentActiveData?._id || ""}
                          value={
                            Math.round(
                              summaryData("entry800g")?.calories +
                                summaryData("protein")?.calories +
                                summaryData("otherFood")?.calories
                            ) || 0
                          }
                          strokeColor={"#FF9431"}
                          maxValue={currentActiveData?.outOfCalories}
                          setIsRefreshNeeded={setIsRefreshNeeded}
                          dayName={activeDay}
                          heightX={
                            currentLevel?.levelName === "level2" ? 210 : 250
                          }
                          widthX={
                            currentLevel?.levelName === "level2" ? 300 : 350
                          }
                        />
                      )}
                    </>
                  )}
                </Box>
                {currentLevel?.levelName === "level3" && (
                  <Box
                    display="flex"
                    gap="40px"
                    justifyContent="center"
                    mb={1.5}
                  >
                    <Button
                      onClick={handleClick}
                      sx={{
                        textTransform: "none",
                        background: "#368AD0",
                        color: "#fcfcfc",
                        height: "40px",
                        fontWeight: 600,
                        width: { lg: "40%", xs: "50%" },
                        padding: "12px 24px",
                        gap: "8px",
                        borderRadius: "4px"
                      }}
                    >
                      <Add /> Add
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      slotProps={{
                        paper: {
                          style: {
                            background: styles.background,
                            color: styles.color
                          }
                        }
                      }}
                      MenuListProps={{
                        "aria-labelledby": "basic-button"
                      }}
                      sx={{
                        "& .MuiPaper-root": {
                          width: anchorEl ? anchorEl.offsetWidth : "auto",
                          background: "#fff",
                          color: "#000"
                          // margin: "0 20px"
                        }
                      }}
                    >
                      <MenuItem
                        sx={{
                          // justifyContent: "center",
                          gap: "10px"
                          // alignItems: "center"
                        }}
                        onClick={() => {
                          handleClose();
                          setEditIngredientData();
                          setIsAddingWeight("entry800g");
                        }}
                      >
                        <Add
                          style={{ marginRight: "4px" }}
                          height={17}
                          width={17}
                        />{" "}
                        <Typography fontSize={12.75}>Add 800g </Typography>
                      </MenuItem>
                      <MenuItem
                        sx={{
                          gap: "10px"
                          // alignItems: "center",
                          // justifyContent: "center"
                        }}
                        onClick={() => {
                          handleClose();
                          setEditIngredientData();
                          setIsAddingWeight("protein");
                        }}
                      >
                        {" "}
                        <Add
                          style={{ marginRight: "4px" }}
                          height={17}
                          width={17}
                        />
                        <Typography fontSize={12.75}>Add Protein</Typography>
                      </MenuItem>
                      <MenuItem
                        sx={{
                          gap: "10px"
                          // alignItems: "center",
                          // justifyContent: "center"
                        }}
                        onClick={() => {
                          handleClose();
                          setEditIngredientData();
                          setIsAddingWeight("otherFood");
                        }}
                      >
                        <Add
                          style={{ marginRight: "4px" }}
                          height={17}
                          width={17}
                        />{" "}
                        <Typography fontSize={12.75}>Add Other</Typography>
                      </MenuItem>
                      <MenuItem
                        sx={{
                          gap: "10px"
                          // alignItems: "center",
                          // justifyContent: "center"
                        }}
                        onClick={() => {
                          handleClose();
                          setIsShowingCreateCustomFood(false);
                          setEditCustomFoodToDiary(true);
                        }}
                      >
                        <Box
                          component="img"
                          src="/images/custom.png"
                          alt="base"
                          sx={{
                            height: 20,
                            width: 20,
                            objectFit: "cover",
                            marginRight: "8px"
                          }}
                        />
                        <Typography fontSize={12.75}>
                          Add Custom Food
                        </Typography>
                      </MenuItem>
                      <MenuItem
                        sx={{
                          gap: "10px"
                          // alignItems: "center",
                          // justifyContent: "center"
                        }}
                        onClick={() => {
                          handleClose();
                          setIsShowingCreateCustomFood(true);
                          setEditOneOffToDiary(true);
                        }}
                      >
                        <Fastfood
                          style={{
                            marginRight: "4px",
                            height: 20,
                            width: 20,
                            color: "wheat"
                          }}
                          height={14}
                          width={14}
                        />{" "}
                        <Typography fontSize={12.75}>Add One Off</Typography>
                      </MenuItem>
                      <MenuItem
                        sx={{
                          gap: "10px"
                          // alignItems: "center"
                        }}
                        onClick={() => {
                          handleClose();
                          // Action for Add Meal
                          setIsShowingCreateMeal(false);
                          setEditMealToDiary(true);
                        }}
                      >
                        <MealIcon
                          style={{ marginRight: "8px" }}
                          height={20}
                          width={20}
                        />
                        <Typography fontSize={12.75}>Add Meal</Typography>
                      </MenuItem>
                      <MenuItem
                        sx={{
                          gap: "10px",
                          alignItems: "center"
                        }}
                        onClick={() => {
                          handleClose();
                          // Action for Add Recipe
                          setIsShowingCreateRecipe(false);
                          setEditRecipeToDiary(true);
                        }}
                      >
                        <RecipeIcon
                          style={{ marginRight: "8px" }}
                          height={17}
                          width={17}
                        />
                        <Typography fontSize={12.75}>Add Recipe</Typography>
                      </MenuItem>
                    </Menu>
                  </Box>
                )}
                {["level1", "level2"]?.includes(selectedLevel) ? (
                  <Box
                    width={"100%"}
                    gap="40px"
                    display="flex"
                    mb={1.5}
                    justifyContent="center"
                  >
                    <Box display="flex" gap="40px">
                      <Button
                        onClick={() => {
                          setEditIngredientData();
                          setIsAddingWeight("entry800g");
                        }}
                        sx={{
                          textTransform: "none",
                          background: "#368AD0",
                          color: "#fcfcfc",
                          height: "40px",
                          fontWeight: 600,
                          padding: "12px 24px",
                          gap: "8px",
                          borderRadius: "4px"
                        }}
                      >
                        <Add /> Add{" "}
                        {currentLevel?.levelName &&
                        currentLevel?.levelName !== "level1"
                          ? `800g`
                          : ""}
                      </Button>
                      {currentLevel?.levelName &&
                      currentLevel?.levelName !== "level1" ? (
                        <Button
                          onClick={() => {
                            setEditIngredientData();
                            setIsAddingWeight("protein");
                          }}
                          sx={{
                            textTransform: "none",
                            background: "#368AD0",
                            color: "#fcfcfc",
                            height: "40px",
                            fontSize: "12px",
                            fontWeight: 600,
                            padding: "12px 24px",
                            gap: "8px",
                            borderRadius: "4px"
                          }}
                        >
                          <Add /> Add Protein
                        </Button>
                      ) : (
                        <></>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <></>
                )}
                {currentActiveData?.entry800g?.length ? (
                  selectedLevel === "level3" ? (
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      gap={2}
                      // mb={1}
                    >
                      <Box
                        display={"flex"}
                        gap={0.4}
                        // flexDirection="column"
                        mt={-2}
                        borderRadius="8px"
                        padding={"12px"}
                        width={"100%"}
                        sx={{ background: styles.lightGreyColor }}
                      >
                        <Box
                          flexDirection="column"
                          display="flex"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                          >
                            <Typography fontWeight="600" color="#f6521c">
                              {"800g"}
                            </Typography>{" "}
                            <IconButton
                              onClick={() => {
                                handleClose();
                                setEditIngredientData();
                                setIsAddingWeight("entry800g");
                              }}
                            >
                              <Add sx={{ color: "#f6521c" }} />
                            </IconButton>
                          </Box>
                          <Typography fontSize={13}>
                            {`${
                              Math.round(+summaryData("entry800g")?.calories) ||
                              0
                            } Cals · ${
                              Math.round(+summaryData("entry800g")?.protein) ||
                              0
                            }g Protein · ${
                              Math.round(
                                +summaryData("entry800g")?.carbohydrate
                              ) || 0
                            }g Carbs · ${
                              Math.round(+summaryData("entry800g")?.fiber) || 0
                            }g Fiber · ${
                              Math.round(+summaryData("entry800g")?.fat) || 0
                            }g Fat`}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        // height={"200px"}
                        gap="10px"
                        borderRadius="16px"
                        p="10px"
                        alignItems="center"
                        display="flex"
                        flexDirection="column"
                        sx={{ background: styles.lightDiaryTracker }}
                        justifyContent="center"
                      >
                        <Box
                          width={"100%"}
                          display="flex"
                          justifyContent="space-between"
                        >
                          <Box />
                          <Typography mr={1} fontSize={"12px"} color="#8E8E8E">
                            (Gram weight)
                          </Typography>
                        </Box>
                        {currentActiveData?.entry800g?.map((item) => (
                          <Box
                            width={"100%"}
                            display="flex"
                            justifyContent="space-between"
                            key={item.key}
                          >
                            <Box display={"flex"} alignItems={"center"}>
                              {showCheckboxes && (
                                <CustomCheckbox
                                  checked={selectedItems.includes(item._id)}
                                  onChange={() =>
                                    handleCheckboxChange(item._id)
                                  }
                                />
                              )}
                              <Box>
                                <Box
                                  display={"flex"}
                                  alignItems="center"
                                  gap="10px"
                                >
                                  <Tooltip
                                    title={
                                      options800gms?.find(
                                        (option) => option.value === item.name
                                      )?.label ?? item.name
                                    }
                                  >
                                    <Typography
                                      fontSize={13}
                                      color={styles.color}
                                      textTransform={"capitalize"}
                                      sx={{
                                        cursor: "pointer",
                                        display: "block", // Ensures the container behaves as a block element
                                        maxWidth: isMobile ? "180px" : "250px", // Set your fixed width (adjust as needed)
                                        overflow: "hidden", // Hide overflow content
                                        textOverflow: "ellipsis", // Show ellipsis when text overflows
                                        whiteSpace: "nowrap" // Prevent text from wrapping
                                      }}
                                    >
                                      {options800gms?.find(
                                        (option) => option.value === item.name
                                      )?.label ?? item.name}
                                    </Typography>
                                  </Tooltip>
                                  {item?.type === "customFood" ||
                                  item?.extraDetails?.isCustomFood ? (
                                    item?.extraDetails?.isFromOneOff ? (
                                      <Fastfood
                                        style={{
                                          color: "wheat",
                                          height: 16,
                                          width: 16
                                        }}
                                      />
                                    ) : (
                                      <Box
                                        component="img"
                                        src="/images/custom.png"
                                        alt="base"
                                        sx={{
                                          height: 16,
                                          width: 16,
                                          objectFit: "cover"
                                        }}
                                      />
                                    )
                                  ) : (
                                    <></>
                                  )}
                                  {item?.type === "meal" ? (
                                    <MealIcon
                                      style={{
                                        width: 16,
                                        height: 16
                                      }}
                                    />
                                  ) : item?.type === "recipe" ? (
                                    <RecipeIcon
                                      style={{
                                        width: 16,
                                        height: 16
                                      }}
                                    />
                                  ) : (
                                    <></>
                                  )}
                                </Box>
                                <Typography
                                  fontSize={10}
                                  color={styles.disabledColor}
                                >
                                  {item.time}
                                </Typography>
                              </Box>
                            </Box>
                            <Box display="flex" alignItems={"center"}>
                              <Typography fontSize={13} fontWeight={600}>
                                {convertItemQuantityToGrams(item)?.quantity}g
                              </Typography>
                              <LongMenu
                                onClickHandler={async (type) => {
                                  setEditIngredientData(item);
                                  if (type === "edit") {
                                    setIsAddingWeight("entry800g");
                                  } else {
                                    await deleteIngredients(
                                      currentLevel?.levelId,
                                      currentActiveData?._id,
                                      item?._id,
                                      activeDay
                                    );
                                    setIsRefreshNeeded(true);
                                    dispatch(
                                      getCurrentUserDiaries({
                                        activeDay,
                                        levelId: currentLevel?.levelId
                                      })
                                    );
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      <Button
                        onClick={async () => {
                          // await updateIngredients({
                          //   diaryId: currentActiveData?._id,
                          //   levelId: currentLevel?.levelId,
                          //   entry800g: [],
                          //   currentDayDate: activeDay
                          // });
                          // setIsRefreshNeeded(true);
                          // dispatch(
                          //   getCurrentUserDiaries({ activeDay,
                          //     levelId: currentLevel?.levelId
                          //   })
                          // );
                          setOpenConfirmModal("entry800g");
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          margin: "0 auto",
                          p: "2px 8px",
                          color: styles.color,
                          border: `1px solid ${styles.color}`,
                          cursor: "pointer",
                          textTransform: "none"
                        }}
                      >
                        {/* <IconButton
                            sx={{
                              color: styles.textColorDiary,
                              height: "17px",
                              width: "17px",
                              padding: 0,
                              ":disabled": {
                                color: styles.textColorDiary
                              }
                            }}
                            disabled
                          >
                            <Add
                              color={styles.textColorDiary}
                              fontSize="12px"
                            />
                          </IconButton> */}
                        <Typography
                          color={styles.textColorDiary}
                          fontSize="13px"
                        >
                          Clear all
                        </Typography>
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Box
                        // height={"200px"}
                        gap="10px"
                        borderRadius="16px"
                        p="10px 20px"
                        mb={1}
                        alignItems="center"
                        display="flex"
                        flexDirection="column"
                        sx={{ background: styles.lightDiaryTracker }}
                        justifyContent="center"
                      >
                        <Typography
                          alignItems={"flex-start"}
                          display={"flex"}
                          fontWeight={600}
                          color={"#f6521c"}
                          width={"100%"}
                        >
                          {"800g"}
                        </Typography>
                        {currentActiveData?.entry800g?.map((item) => (
                          <Box
                            width={"100%"}
                            display="flex"
                            justifyContent="space-between"
                            key={item.key}
                          >
                            <Box display={"flex"} alignItems={"center"}>
                              {showCheckboxes && (
                                <CustomCheckbox
                                  checked={selectedItems.includes(item._id)}
                                  onChange={() =>
                                    handleCheckboxChange(item._id)
                                  }
                                />
                              )}
                              <Box>
                                <Typography
                                  fontSize={13}
                                  color={styles.color}
                                  textTransform={"capitalize"}
                                >
                                  {options800gms?.find(
                                    (option) => option.value === item.name
                                  )?.label ?? item.name}
                                </Typography>
                                <Typography
                                  fontSize={10}
                                  color={styles.disabledColor}
                                >
                                  {item.time}
                                </Typography>
                              </Box>
                            </Box>
                            <Box display="flex" alignItems={"center"}>
                              <Typography fontSize={13} fontWeight={600}>
                                {convertItemQuantityToGrams(item)?.quantity}g
                              </Typography>
                              <LongMenu
                                onClickHandler={async (type) => {
                                  setEditIngredientData(item);
                                  if (type === "edit") {
                                    setIsAddingWeight("entry800g");
                                  } else {
                                    await deleteIngredients(
                                      currentLevel?.levelId,
                                      currentActiveData?._id,
                                      item?._id,
                                      activeDay
                                    );
                                    setIsRefreshNeeded(true);
                                    dispatch(
                                      getCurrentUserDiaries({
                                        activeDay,
                                        levelId: currentLevel?.levelId
                                      })
                                    );
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      <Button
                        onClick={async () => {
                          // await updateIngredients({
                          //   diaryId: currentActiveData?._id,
                          //   levelId: currentLevel?.levelId,
                          //   entry800g: [],
                          //   currentDayDate: activeDay
                          // });
                          // setIsRefreshNeeded(true);
                          // dispatch(
                          //   getCurrentUserDiaries({ activeDay,
                          //     levelId: currentLevel?.levelId
                          //   })
                          // );
                          setOpenConfirmModal("entry800g");
                        }}
                        sx={{
                          margin: "0 auto",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          p: "2px 8px",
                          color: styles.color,
                          border: `1px solid ${styles.color}`,
                          cursor: "pointer",
                          textTransform: "none"
                        }}
                      >
                        {/* <IconButton
                      sx={{
                        color: styles.textColorDiary,
                        height: "17px",
                        width: "17px",
                        padding: 0,
                        ":disabled": {
                          color: styles.textColorDiary
                        }
                      }}
                      disabled
                    >
                      <Add color={styles.textColorDiary} fontSize="12px" />
                    </IconButton> */}
                        <Typography
                          color={styles.textColorDiary}
                          fontSize="13px"
                        >
                          Clear all
                        </Typography>
                      </Button>
                    </Box>
                  )
                ) : (
                  <Box
                    gap="10px"
                    borderRadius="16px"
                    p="10px 20px"
                    mb={1}
                    alignItems="center"
                    display="flex"
                    flexDirection="column"
                    sx={{ background: styles.lightDiaryTracker }}
                    justifyContent="center"
                  >
                    <Box
                      display="flex"
                      width={"100%"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Typography
                        alignItems={"flex-start"}
                        display={"flex"}
                        fontWeight={600}
                        color={"#f6521c"}
                        width={"100%"}
                      >
                        {"800g"}
                      </Typography>
                      {selectedLevel === "level3" && (
                        <IconButton
                          onClick={() => {
                            handleClose();
                            setEditIngredientData();
                            setIsAddingWeight("entry800g");
                          }}
                        >
                          <Add sx={{ color: "#f6521c" }} />
                        </IconButton>
                      )}
                    </Box>

                    <NoEntry />
                    <Typography color="#6B7280">No entry</Typography>
                  </Box>
                )}
                {currentLevel?.levelName &&
                  currentLevel?.levelName !== "level1" && (
                    <>
                      {currentActiveData?.protein?.length ? (
                        selectedLevel === "level3" ? (
                          <Box
                            mb={1}
                            display={"flex"}
                            flexDirection={"column"}
                            gap={2}
                          >
                            <Box
                              display={"flex"}
                              gap={0.4}
                              // flexDirection="column"
                              mt={-2}
                              justifyContent={"space-between"}
                              borderRadius="8px"
                              padding={"12px"}
                              width={"100%"}
                              sx={{ background: styles.lightGreyColor }}
                            >
                              <Box
                                flexDirection="column"
                                display="flex"
                                justifyContent="space-between"
                                width="100%"
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  width="100%"
                                >
                                  <Typography fontWeight="600" color="#368AD0">
                                    {"Protein"}
                                  </Typography>{" "}
                                  <IconButton
                                    onClick={() => {
                                      handleClose();
                                      setEditIngredientData();
                                      setIsAddingWeight("protein");
                                    }}
                                  >
                                    <Add sx={{ color: "#368AD0" }} />
                                  </IconButton>
                                </Box>
                                <Typography fontSize={13}>
                                  {`${
                                    Math.round(
                                      +summaryData("protein")?.calories
                                    ) || 0
                                  } Cals · ${
                                    Math.round(
                                      +summaryData("protein")?.protein
                                    ) || 0
                                  }g Protein · ${
                                    Math.round(
                                      +summaryData("protein")?.carbohydrate
                                    ) || 0
                                  }g Carbs · ${
                                    Math.round(
                                      +summaryData("protein")?.fiber
                                    ) || 0
                                  }g Fiber · ${
                                    Math.round(+summaryData("protein")?.fat) ||
                                    0
                                  }g Fat`}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              gap="10px"
                              borderRadius="16px"
                              p="10px"
                              alignItems="center"
                              display="flex"
                              flexDirection="column"
                              sx={{ background: styles.lightDiaryTracker }}
                              justifyContent="center"
                            >
                              {" "}
                              <Box
                                width={"100%"}
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Box />
                                <Typography
                                  mr={1}
                                  fontSize={"12px"}
                                  color="#8E8E8E"
                                >
                                  (Protein grams)
                                </Typography>
                              </Box>
                              {currentActiveData?.protein?.map((item) => (
                                <Box
                                  width={"100%"}
                                  display="flex"
                                  justifyContent="space-between"
                                  key={item.key}
                                >
                                  <Box display={"flex"} alignItems={"center"}>
                                    {showCheckboxes && (
                                      <CustomCheckbox
                                        checked={selectedItems.includes(
                                          item._id
                                        )}
                                        onChange={() =>
                                          handleCheckboxChange(item._id)
                                        }
                                      />
                                    )}
                                    <Box>
                                      <Box
                                        display={"flex"}
                                        alignItems="center"
                                        gap="10px"
                                      >
                                        <Tooltip
                                          title={
                                            optionsProteins?.find(
                                              (option) =>
                                                option.value === item.name
                                            )?.label ?? item.name
                                          }
                                        >
                                          <Typography
                                            sx={{
                                              cursor: "pointer",
                                              display: "block", // Ensures the container behaves as a block element
                                              maxWidth: isMobile
                                                ? "210px"
                                                : "600px", // Set your fixed width (adjust as needed)
                                              overflow: "hidden", // Hide overflow content
                                              textOverflow: "ellipsis", // Show ellipsis when text overflows
                                              whiteSpace: "nowrap" // Prevent text from wrapping
                                            }}
                                            fontSize={13}
                                            color={styles.color}
                                          >
                                            {optionsProteins?.find(
                                              (option) =>
                                                option.value === item.name
                                            )?.label ?? item.name}
                                          </Typography>
                                        </Tooltip>
                                        {item?.type === "customFood" ||
                                        item?.extraDetails?.isCustomFood ? (
                                          item?.extraDetails?.isFromOneOff ? (
                                            <Fastfood
                                              style={{
                                                color: "wheat",
                                                height: 16,
                                                width: 16
                                              }}
                                            />
                                          ) : (
                                            <Box
                                              component="img"
                                              src="/images/custom.png"
                                              alt="base"
                                              sx={{
                                                height: 16,
                                                width: 16,
                                                objectFit: "cover"
                                              }}
                                            />
                                          )
                                        ) : (
                                          <></>
                                        )}
                                        {item?.type === "meal" ? (
                                          <MealIcon
                                            style={{
                                              width: 16,
                                              height: 16
                                            }}
                                          />
                                        ) : item?.type === "recipe" ? (
                                          <RecipeIcon
                                            style={{
                                              width: 16,
                                              height: 16
                                            }}
                                          />
                                        ) : (
                                          <></>
                                        )}
                                      </Box>
                                      <Typography
                                        fontSize={10}
                                        color={styles.disabledColor}
                                      >
                                        {item.time}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box display="flex" alignItems={"center"}>
                                    <Typography fontSize={13} fontWeight={600}>
                                      {Math.round(
                                        updatedItemAccordingQuantity(item)
                                          ?.extraDetails?.serving?.protein
                                      )}
                                      g
                                    </Typography>
                                    <LongMenu
                                      onClickHandler={async (type) => {
                                        setEditIngredientData(item);
                                        if (type === "edit") {
                                          setIsAddingWeight("protein");
                                        } else {
                                          await deleteIngredients(
                                            currentLevel?.levelId,
                                            currentActiveData?._id,
                                            item?._id,
                                            activeDay
                                          );
                                          setIsRefreshNeeded(true);
                                          dispatch(
                                            getCurrentUserDiaries({
                                              activeDay,
                                              levelId: currentLevel?.levelId
                                            })
                                          );
                                        }
                                      }}
                                    />
                                  </Box>
                                </Box>
                              ))}
                            </Box>{" "}
                            <Button
                              onClick={async () => {
                                // await updateIngredients({
                                //   diaryId: currentActiveData?._id,
                                //   levelId: currentLevel?.levelId,
                                //   protein: [],
                                //   currentDayDate: activeDay
                                // });
                                // setIsRefreshNeeded(true);
                                // dispatch(
                                //   getCurrentUserDiaries({ activeDay,
                                //     levelId: currentLevel?.levelId
                                //   })
                                // );
                                setOpenConfirmModal("protein");
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                p: "2px 8px",
                                margin: "0 auto",
                                color: styles.color,
                                border: `1px solid ${styles.color}`,
                                cursor: "pointer",
                                textTransform: "none"
                              }}
                            >
                              {/* <IconButton
                                sx={{
                                  color: styles.textColorDiary,
                                  height: "17px",
                                  width: "17px",
                                  padding: 0,
                                  ":disabled": {
                                    color: styles.textColorDiary
                                  }
                                }}
                                disabled
                              >
                                <Add
                                  color={styles.textColorDiary}
                                  fontSize="12px"
                                />
                              </IconButton> */}
                              <Typography
                                color={styles.textColorDiary}
                                fontSize="13px"
                              >
                                Clear all
                              </Typography>
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            <Box
                              gap="10px"
                              borderRadius="16px"
                              p="10px 20px"
                              alignItems="center"
                              mb={1}
                              display="flex"
                              flexDirection="column"
                              sx={{ background: styles.lightDiaryTracker }}
                              justifyContent="center"
                            >
                              <Typography
                                width={"100%"}
                                display={"flex"}
                                justifyContent={"flex-start"}
                                fontWeight={600}
                                color="#368AD0"
                              >
                                Protein{" "}
                              </Typography>
                              {currentActiveData?.protein?.map((item) => (
                                <Box
                                  width={"100%"}
                                  display="flex"
                                  justifyContent="space-between"
                                  key={item.key}
                                >
                                  <Box display={"flex"} alignItems={"center"}>
                                    {showCheckboxes && (
                                      <CustomCheckbox
                                        checked={selectedItems.includes(
                                          item._id
                                        )}
                                        onChange={() =>
                                          handleCheckboxChange(item._id)
                                        }
                                      />
                                    )}
                                    <Box>
                                      <Typography
                                        fontSize={13}
                                        color={styles.color}
                                        textTransform={"capitalize"}
                                      >
                                        {optionsProteins?.find(
                                          (option) => option.value === item.name
                                        )?.label ?? item.name}
                                      </Typography>
                                      <Typography
                                        fontSize={10}
                                        color={styles.disabledColor}
                                      >
                                        {item.time}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box display="flex" alignItems={"center"}>
                                    <Typography fontSize={13} fontWeight={600}>
                                      {
                                        convertItemQuantityToGrams(item)
                                          ?.quantity
                                      }
                                      g
                                    </Typography>
                                    {!item?.extraProteinAdded ? (
                                      <LongMenu
                                        onClickHandler={async (type) => {
                                          if (type === "edit") {
                                            setEditIngredientData(item);
                                            setIsAddingWeight("protein");
                                          } else {
                                            await deleteIngredients(
                                              currentLevel?.levelId,
                                              currentActiveData?._id,
                                              item?._id,
                                              activeDay
                                            );
                                            setIsRefreshNeeded(true);
                                            dispatch(
                                              getCurrentUserDiaries({
                                                activeDay,
                                                levelId: currentLevel?.levelId
                                              })
                                            );
                                          }
                                        }}
                                      />
                                    ) : (
                                      <Box height={28} width={32}></Box>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                            <Button
                              onClick={async () => {
                                // await updateIngredients({
                                //   diaryId: currentActiveData?._id,
                                //   levelId: currentLevel?.levelId,
                                //   protein: [],
                                //   currentDayDate: activeDay
                                // });
                                // setIsRefreshNeeded(true);
                                // dispatch(
                                //   getCurrentUserDiaries({ activeDay,
                                //     levelId: currentLevel?.levelId
                                //   })
                                // );
                                setOpenConfirmModal("protein");
                              }}
                              sx={{
                                margin: "0 auto",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                p: "2px 8px",
                                color: styles.color,
                                border: `1px solid ${styles.color}`,
                                cursor: "pointer",
                                textTransform: "none"
                              }}
                            >
                              {/* <IconButton
                             sx={{
                               color: styles.textColorDiary,
                               height: "17px",
                               width: "17px",
                               padding: 0,
                               ":disabled": {
                                 color: styles.textColorDiary
                               }
                             }}
                             disabled
                           >
                             <Add
                               color={styles.textColorDiary}
                               fontSize="12px"
                             />
                           </IconButton> */}
                              <Typography
                                color={styles.textColorDiary}
                                fontSize="13px"
                              >
                                Clear all
                              </Typography>
                            </Button>
                          </Box>
                        )
                      ) : (
                        <Box
                          gap="10px"
                          borderRadius="16px"
                          p="10px 20px"
                          alignItems="center"
                          mb={1}
                          display="flex"
                          flexDirection="column"
                          sx={{ background: styles.lightDiaryTracker }}
                          justifyContent="center"
                        >
                          <Box
                            display="flex"
                            width={"100%"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                          >
                            <Typography
                              alignItems={"flex-start"}
                              display={"flex"}
                              fontWeight={600}
                              color={"#368AD0"}
                              width={"100%"}
                            >
                              {"Protein"}
                            </Typography>
                            {selectedLevel === "level3" && (
                              <IconButton
                                onClick={() => {
                                  handleClose();
                                  setEditIngredientData();
                                  setIsAddingWeight("protein");
                                }}
                              >
                                <Add sx={{ color: "#368AD0" }} />
                              </IconButton>
                            )}
                          </Box>
                          <NoEntry />
                          <Typography color="#6B7280">No entry</Typography>
                        </Box>
                      )}
                    </>
                  )}
                {currentLevel?.levelName === "level3" && (
                  <>
                    {currentActiveData?.otherFood?.length ? (
                      <Box display={"flex"} flexDirection={"column"} gap={2}>
                        <Box
                          display={"flex"}
                          gap={0.4}
                          // flexDirection="column"
                          mt={-2}
                          borderRadius="8px"
                          padding={"12px"}
                          width={"100%"}
                          sx={{ background: styles.lightGreyColor }}
                        >
                          <Box
                            flexDirection="column"
                            display="flex"
                            justifyContent="space-between"
                            width="100%"
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              width="100%"
                            >
                              <Typography fontWeight={600} color={"#ff9431"}>
                                {"Other"}
                              </Typography>
                              <IconButton
                                onClick={() => {
                                  handleClose();
                                  setEditIngredientData();
                                  setIsAddingWeight("otherFood");
                                }}
                              >
                                <Add sx={{ color: "#FF9431" }} />
                              </IconButton>
                            </Box>
                            <Typography fontSize={13}>
                              {`${
                                Math.round(
                                  +summaryData("otherFood")?.calories
                                ) || 0
                              } Cals · ${
                                Math.round(
                                  +summaryData("otherFood")?.protein
                                ) || 0
                              }g Protein · ${
                                Math.round(
                                  +summaryData("otherFood")?.carbohydrate
                                ) || 0
                              }g Carbs · ${
                                Math.round(+summaryData("otherFood")?.fiber) ||
                                0
                              }g Fiber · ${
                                Math.round(+summaryData("otherFood")?.fat) || 0
                              }g Fat`}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          gap="10px"
                          borderRadius="16px"
                          p="10px"
                          alignItems="center"
                          display="flex"
                          flexDirection="column"
                          sx={{ background: styles.lightDiaryTracker }}
                          justifyContent="center"
                        >
                          {" "}
                          <Box
                            width={"100%"}
                            display="flex"
                            justifyContent="space-between"
                          >
                            <Box />
                            <Typography
                              mr={1}
                              fontSize={"12px"}
                              color="#8E8E8E"
                            >
                              (Calories)
                            </Typography>
                          </Box>
                          {currentActiveData?.otherFood?.map((item) => (
                            <Box
                              width={"100%"}
                              display="flex"
                              justifyContent="space-between"
                              key={item.key}
                            >
                              <Box display={"flex"} alignItems={"center"}>
                                {showCheckboxes && (
                                  <CustomCheckbox
                                    checked={selectedItems.includes(item._id)}
                                    onChange={() =>
                                      handleCheckboxChange(item._id)
                                    }
                                  />
                                )}
                                <Box>
                                  <Box
                                    display={"flex"}
                                    alignItems="center"
                                    gap="10px"
                                  >
                                    <Tooltip title={item.name}>
                                      <Typography
                                        fontSize={13}
                                        color={styles.color}
                                        textTransform={"capitalize"}
                                        sx={{
                                          cursor: "pointer",
                                          display: "block", // Ensures the container behaves as a block element
                                          maxWidth: isMobile
                                            ? "160px"
                                            : "210px", // Set your fixed width (adjust as needed)
                                          overflow: "hidden", // Hide overflow content
                                          textOverflow: "ellipsis", // Show ellipsis when text overflows
                                          whiteSpace: "nowrap" // Prevent text from wrapping
                                        }}
                                      >
                                        {item.name}
                                      </Typography>
                                    </Tooltip>
                                    {item?.type === "customFood" ||
                                    item?.extraDetails?.isCustomFood ? (
                                      item?.extraDetails?.isFromOneOff ? (
                                        <Fastfood
                                          style={{
                                            color: "wheat",
                                            height: 16,
                                            width: 16
                                          }}
                                        />
                                      ) : (
                                        <Box
                                          component="img"
                                          src="/images/custom.png"
                                          alt="base"
                                          sx={{
                                            height: 16,
                                            width: 16,
                                            objectFit: "cover"
                                          }}
                                        />
                                      )
                                    ) : (
                                      <></>
                                    )}
                                    {item?.type === "meal" ? (
                                      <MealIcon
                                        style={{
                                          width: 16,
                                          height: 16
                                        }}
                                      />
                                    ) : item?.type === "recipe" ? (
                                      <RecipeIcon
                                        style={{
                                          width: 16,
                                          height: 16
                                        }}
                                      />
                                    ) : (
                                      <></>
                                    )}
                                  </Box>
                                  <Typography
                                    fontSize={10}
                                    color={styles.disabledColor}
                                  >
                                    {item.time}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box display="flex" alignItems={"center"}>
                                <Typography fontSize={13} fontWeight={600}>
                                  {Math.round(
                                    updatedItemAccordingQuantity(item)
                                      ?.extraDetails?.serving?.calories
                                  )}
                                </Typography>
                                <LongMenu
                                  onClickHandler={async (type) => {
                                    setEditIngredientData(item);
                                    if (type === "edit") {
                                      setIsAddingWeight("otherFood");
                                    } else {
                                      await deleteIngredients(
                                        currentLevel?.levelId,
                                        currentActiveData?._id,
                                        item?._id,
                                        activeDay
                                      );
                                      setIsRefreshNeeded(true);
                                      dispatch(
                                        getCurrentUserDiaries({
                                          activeDay,
                                          levelId: currentLevel?.levelId
                                        })
                                      );
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                        <Button
                          onClick={async () => {
                            // await updateIngredients({
                            //   diaryId: currentActiveData?._id,
                            //   levelId: currentLevel?.levelId,
                            //   otherFood: [],
                            //   currentDayDate: activeDay
                            // });
                            // setIsRefreshNeeded(true);
                            // dispatch(
                            //   getCurrentUserDiaries({ activeDay,
                            //     levelId: currentLevel?.levelId
                            //   })
                            // );
                            setOpenConfirmModal("otherFood");
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            p: "2px 8px",
                            margin: "0 auto",
                            color: styles.color,
                            border: `1px solid ${styles.color}`,
                            cursor: "pointer",
                            textTransform: "none"
                          }}
                        >
                          {/* <IconButton
                                sx={{
                                  color: styles.textColorDiary,
                                  height: "17px",
                                  width: "17px",
                                  padding: 0,
                                  ":disabled": {
                                    color: styles.textColorDiary
                                  }
                                }}
                                disabled
                              >
                                <Add
                                  color={styles.textColorDiary}
                                  fontSize="12px"
                                />
                              </IconButton> */}
                          <Typography
                            color={styles.textColorDiary}
                            fontSize="13px"
                          >
                            Clear all
                          </Typography>
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        gap="10px"
                        borderRadius="16px"
                        p="10px 20px"
                        alignItems="center"
                        display="flex"
                        flexDirection="column"
                        sx={{ background: styles.lightDiaryTracker }}
                        justifyContent="center"
                      >
                        <Box
                          display="flex"
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                        >
                          <Typography
                            alignItems={"flex-start"}
                            display={"flex"}
                            fontWeight={600}
                            color={"#ff9431"}
                            width={"100%"}
                          >
                            {"Other"}
                          </Typography>
                          {selectedLevel === "level3" && (
                            <IconButton
                              onClick={() => {
                                handleClose();
                                setEditIngredientData();
                                setIsAddingWeight("otherFood");
                              }}
                            >
                              <Add sx={{ color: "#ff9431" }} />
                            </IconButton>
                          )}
                        </Box>
                        <NoEntry />
                        <Typography color="#6B7280">No entry</Typography>
                      </Box>
                    )}
                  </>
                )}

                {!currentActiveData?.notes && !isAddingNote && (
                  <Button
                    onClick={() => setIsAddingNote(true)}
                    sx={{
                      width: "120px",
                      textTransform: "none",
                      color: "#fcfcfc",
                      height: "40px",
                      fontWeight: 600,
                      padding: "12px",
                      gap: "8px",
                      borderRadius: "4px"
                    }}
                  >
                    <Add sx={{ height: 16, width: 16, color: styles.yellow }} />
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 600 }}
                      color={styles.yellow}
                    >
                      Add Note
                    </Typography>
                  </Button>
                )}

                {(isAddingNote || currentActiveData?.notes) && (
                  <Box
                    width="100%"
                    display="flex"
                    flexDirection="column"
                    gap="10px"
                    sx={{
                      background: styles.lightDiaryTracker,
                      borderRadius: "8px",
                      p: "12px 20px"
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        fontWeight={600}
                        fontSize="16px"
                        color={styles.yellow}
                      >
                        Notes
                      </Typography>
                      {!isAddingNote && currentActiveData?.notes && (
                        <IconButton
                          onClick={() => setIsAddingNote(true)}
                          size="small"
                        >
                          <Edit sx={{ color: styles.yellow, fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>

                    {isAddingNote ? (
                      <>
                        <Box position="relative" mt={0}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={10}
                            variant="outlined"
                            placeholder="Write your note..."
                            value={noteText}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              if (newValue.length <= 350) {
                                setNoteText(newValue);
                              }
                            }}
                            onKeyDown={(e) => {
                              // This is optional — only needed if you want to do something on Enter
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault(); // prevent parent form submit if needed
                                // Optionally, handle enter-specific logic here
                              }
                            }}
                            InputProps={{
                              sx: {
                                color: styles.color,
                                "& fieldset": {
                                  borderColor: styles.notesColor
                                },
                                "&:hover fieldset": {
                                  borderColor: styles.notesColor
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: styles.notesColor
                                },
                                borderRadius: "4px"
                              }
                            }}
                            sx={{
                              backgroundColor: "transparent",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: styles.notesColor
                                },
                                "&:hover fieldset": {
                                  borderColor: styles.notesColor
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: styles.notesColor
                                }
                              },
                              "& .MuiInputBase-input": {
                                color: styles.color
                              }
                            }}
                          />
                        </Box>

                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          gap="10px"
                        >
                          <IconButton
                            onClick={async () => {
                              await updateIngredients({
                                diaryId: currentActiveData?._id,
                                levelId: currentActiveData?.levelId,
                                currentDayDate:
                                  currentActiveData?.currentDayDate,
                                notes: noteText?.trim()
                              });
                              setIsAddingNote(false);
                              setIsRefreshNeeded(true);
                              dispatch(
                                getCurrentUserDiaries({
                                  activeDay,
                                  levelId: currentLevel?.levelId
                                })
                              );
                              // Optionally: call your saveNote function here
                            }}
                          >
                            <Check sx={{ color: "#28a745" }} />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setIsAddingNote(false);
                              // setNoteText(currentActiveData?.notes || "");
                            }}
                          >
                            <Close sx={{ color: "#dc3545" }} />
                          </IconButton>
                        </Box>
                      </>
                    ) : (
                      <Typography
                        fontSize="14px"
                        color={styles.inputLabelColor}
                      >
                        {currentActiveData.notes}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      )}
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarMessage.includes("Success") ? "success" : "error"}
          variant="filled"
          sx={{
            width: "100%",
            alignItems: "center",
            padding: "0 16px",
            ".MuiAlert-action": {
              padding: "8px"
            }
          }}
        >
          <Box display="flex" gap="10px" alignItems={"center"}>
            <Typography fontSize="14px"> {snackbarMessage}</Typography>{" "}
            {!snackbarMessage?.includes("yesterday") &&
            !snackbarMessage?.includes("entry") &&
            !snackbarMessage?.includes("ingredients") &&
            !snackbarMessage?.includes("subscription") ? (
              <Button
                onClick={() => navigate("/programs")}
                sx={{
                  color: styles.color,
                  textTransform: "none",
                  background: "#F87171", // red-400
                  "&:hover": {
                    background: "#FCA5A5" // red-300
                  }
                }}
              >
                Go here
              </Button>
            ) : (
              <></>
            )}
          </Box>
        </Alert>
      </Snackbar>
      <ConfirmModal
        styles={styles}
        open={!!openConfirmModal}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirm}
      />
    </Box>
  );
};

export default Diary;
